import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import FaceCapturePanel from "../components/FaceCapturePanel";
import HraHumanBodyViewer from "../components/HraHumanBodyViewer";
import PatientResearchContextPanel from "../components/PatientResearchContextPanel";
import D3OrganTreePanel from "../components/D3OrganTreePanel";
import BioPeriodicTable from "../components/researchLab/BioPeriodicTable";
import BioMoleculeViewer3D from "../components/researchLab/BioMoleculeViewer3D";
import MolecularResearchStudio from "../components/MolecularResearchStudio";
import DNAResearchStudio from "../components/DNAResearchStudio";
import "./ResearchPage.responsive.css";

const emptyChemical = {
  name: "",
  formula: "",
  molecular_weight: "",
  category: "compound",
  description: "",
};

const emptyImpact = {
  chemical_id: "",
  organ: "liver",
  system: "hepatic",
  impact_type: "toxic",
  severity: "0.4",
  description: "",
};

const ORGAN_OPTIONS = [
  "brain",
  "spinal_cord",
  "eye",
  "ear",
  "pituitary",
  "lungs",
  "trachea",
  "bronchi",
  "diaphragm",
  "heart",
  "aorta",
  "artery",
  "vein",
  "blood",
  "vascular",
  "mouth",
  "esophagus",
  "stomach",
  "small_intestine",
  "large_intestine",
  "intestine",
  "colon",
  "rectum",
  "liver",
  "gallbladder",
  "pancreas",
  "spleen",
  "kidney",
  "ureter",
  "bladder",
  "urethra",
  "thyroid",
  "parathyroid",
  "adrenal",
  "thymus",
  "lymph_node",
  "lymph",
  "bone_marrow",
  "bone",
  "skeleton",
  "skull",
  "rib",
  "spine",
  "vertebra",
  "pelvis",
  "femur",
  "tibia",
  "humerus",
  "muscle",
  "cartilage",
  "joint",
  "skin",
  "hair",
  "nail",
  "ovaries",
  "uterus",
  "fallopian_tube",
  "cervix",
  "vagina",
  "breast",
  "testes",
  "penis",
  "prostate",
  "seminal_vesicle",
  "vas_deferens",
];

const researchInlineCss = `
.research-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-panel: #0b1829;
  --mg-blue: #1f7bc1;
  --mg-blue-2: #2d8fe0;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;
  --mg-green: #10b981;
  --mg-red: #ef4444;
  --mg-yellow: #f59e0b;

  width: 100%;
  min-height: calc(100vh - 68px);
  padding: 28px 34px 46px;
  color: var(--mg-text);
  background:
    radial-gradient(circle at 10% 0%, rgba(24, 183, 255, 0.10), transparent 28%),
    linear-gradient(180deg, #f7fbff 0%, #eef6ff 100%);
}

.research-page * {
  box-sizing: border-box;
}

.research-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
  align-items: stretch;
  margin-bottom: 18px;
  padding: 26px;
  border-radius: 24px;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.10), transparent 30%),
    rgba(255, 255, 255, 0.96);
  border: 1px solid var(--mg-border);
  box-shadow: 0 20px 55px rgba(7, 17, 31, 0.07);
}

.research-kicker,
.bio-studio-kicker {
  margin: 0 0 10px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.research-hero h1 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(30px, 3vw, 46px);
  line-height: 1.05;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.research-hero p {
  max-width: 900px;
  margin: 12px 0 14px;
  color: var(--mg-muted);
  font-size: 14px;
  line-height: 1.65;
}

.research-mini-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.research-mini-stats span {
  min-height: 27px;
  display: inline-flex;
  align-items: center;
  padding: 6px 11px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 11px;
  font-weight: 900;
}

.research-warning {
  min-height: 130px;
  padding: 22px;
  border-radius: 20px;
  color: white;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.18), transparent 32%),
    linear-gradient(145deg, var(--mg-black), var(--mg-navy-2));
  border: 1px solid rgba(24, 183, 255, 0.20);
  display: grid;
  align-content: center;
  gap: 8px;
}

.research-warning strong {
  color: white;
  font-size: 16px;
  font-weight: 950;
}

.research-warning span {
  color: #a9bdd3;
  line-height: 1.45;
}

.research-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 18px 0;
}

.research-tabs button,
.bio-library-tabs button,
.bio-workbench-mode-tabs button,
.avatar-test-actions button,
.impact-step-tabs button {
  min-height: 38px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid var(--mg-border);
  background: rgba(255, 255, 255, 0.92);
  color: #112238;
  cursor: pointer;
  font-size: 12px;
  font-weight: 900;
  transition: 0.16s ease;
}

.research-tabs button.active,
.bio-library-tabs button.active,
.bio-workbench-mode-tabs button.active,
.impact-step-tabs button.active,
.avatar-test-actions button.primary {
  color: white;
  background: linear-gradient(135deg, var(--mg-blue), #0f5f9d);
  border-color: transparent;
  box-shadow: 0 12px 26px rgba(31, 123, 193, 0.22);
}

.research-layout-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 18px;
  align-items: start;
}

.research-left {
  display: grid;
  gap: 18px;
  min-width: 0;
}

.research-right {
  min-width: 0;
}

.sticky-panel {
  position: sticky;
  top: 84px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  scrollbar-width: thin;
}

.research-card {
  border: 1px solid var(--mg-border);
  border-radius: 22px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.045), transparent 28%),
    rgba(255, 255, 255, 0.96);
  box-shadow: 0 16px 44px rgba(7, 17, 31, 0.06);
  padding: 20px;
}

.research-card.wide {
  width: 100%;
}

.research-card h2 {
  margin: 0;
  color: var(--mg-text);
  font-size: 22px;
  line-height: 1.12;
  letter-spacing: -0.04em;
  font-weight: 950;
}

.research-card h3 {
  margin: 0;
  color: var(--mg-text);
  font-size: 17px;
  font-weight: 950;
}

.research-card p,
.muted {
  color: var(--mg-muted);
  line-height: 1.55;
}

.research-card-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.research-card-head p {
  margin: 7px 0 0;
}

.selected-drugs-open-btn,
.research-primary,
.impact-secondary-btn,
.ask-llm-btn,
.impact-next-btn {
  min-height: 40px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 950;
  transition: 0.16s ease;
}

.research-primary,
.ask-llm-btn,
.impact-next-btn {
  color: white;
  background: linear-gradient(135deg, var(--mg-blue), #0f5f9d);
  box-shadow: 0 12px 26px rgba(31, 123, 193, 0.22);
}

.impact-secondary-btn,
.selected-drugs-open-btn {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

.research-primary:disabled,
.ask-llm-btn:disabled,
.avatar-test-actions button:disabled {
  opacity: 0.56;
  cursor: not-allowed;
}

.chemical-search-bar,
.bio-library-search,
.assist-search-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 10px;
  margin-bottom: 12px;
}

.chemical-search-bar input,
.bio-library-search input,
.assist-search-row input,
.research-input,
.impact-field input,
.impact-field select,
.impact-field textarea,
.studio-picker-search {
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  border-radius: 11px;
  border: 1px solid var(--mg-border);
  background: rgba(255, 255, 255, 0.96);
  color: #112238;
  font-size: 13px;
  outline: none;
}

.chemical-search-bar button,
.bio-library-search button,
.assist-search-row button {
  min-height: 40px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  color: white;
  background: linear-gradient(135deg, var(--mg-blue), #0f5f9d);
  cursor: pointer;
  font-size: 12px;
  font-weight: 900;
}

.chemical-search-bar button.secondary {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

.chemical-az-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 12px;
}

.chemical-az-filter button {
  width: 31px;
  height: 31px;
  border-radius: 9px;
  border: 1px solid var(--mg-border);
  color: #0b4e84;
  background: #eef6ff;
  cursor: pointer;
  font-size: 11px;
  font-weight: 900;
}

.chemical-az-filter button.active {
  color: white;
  background: var(--mg-blue);
}

.chemical-pagination-info {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  color: var(--mg-muted);
  font-size: 11px;
  font-weight: 850;
}

.digital-avatar-reason-card,
.research-face-summary-card,
.selected-patient-chip,
.bmi-chip {
  margin: 12px 0;
  padding: 15px;
  border-radius: 16px;
  background: #f4f9ff;
  border: 1px solid #d9e8f6;
}

.digital-avatar-reason-card strong,
.research-face-summary-card strong {
  display: block;
  color: var(--mg-text);
  font-size: 14px;
  font-weight: 950;
}

.digital-avatar-reason-card p,
.research-face-summary-card p {
  margin: 7px 0 0;
  color: var(--mg-muted);
  font-size: 12px;
}

.chemical-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(178px, 1fr));
  gap: 12px;
}

.chemical-card,
.bio-select-card,
.bio-library-item,
.queue-card,
.history-tile {
  min-height: 120px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid var(--mg-border);
  background: rgba(255, 255, 255, 0.96);
  cursor: pointer;
  text-align: left;
  transition: 0.16s ease;
}

.chemical-card:hover,
.bio-select-card:hover,
.bio-library-item:hover,
.queue-card:hover,
.history-tile:hover {
  transform: translateY(-2px);
  border-color: rgba(31, 123, 193, 0.55);
  box-shadow: 0 14px 30px rgba(31, 123, 193, 0.10);
}

.chemical-card.selected,
.bio-select-card.selected,
.bio-library-item.active,
.history-tile.active {
  border-color: var(--mg-blue);
  background: #f2f8ff;
  box-shadow: 0 0 0 3px rgba(31, 123, 193, 0.10);
}

.chemical-card-top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.chemical-card strong,
.bio-select-card strong,
.bio-library-item strong,
.queue-card strong,
.history-tile strong {
  display: block;
  color: var(--mg-text);
  font-size: 13px;
  font-weight: 950;
  line-height: 1.25;
}

.chemical-card span,
.chemical-card-top span,
.bio-select-card span,
.bio-library-item span,
.queue-card span,
.history-tile span {
  color: #0b4e84;
  font-size: 10px;
  font-weight: 850;
}

.chemical-card-top span {
  padding: 4px 7px;
  border-radius: 999px;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.14);
}

.chemical-card p,
.bio-select-card p,
.queue-card p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  font-size: 11px;
  line-height: 1.4;
}

.dose-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 7px;
  align-items: center;
  margin-top: 10px;
}

.dose-row label,
.research-label,
.impact-field label {
  display: block;
  margin: 10px 0 6px;
  color: #44576e;
  font-size: 11px;
  font-weight: 950;
}

.dose-row input {
  min-height: 32px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid var(--mg-border);
}

.chemical-pagination {
  display: flex;
  justify-content: center;
  gap: 16px;
  align-items: center;
  margin-top: 16px;
  padding: 12px;
  border-radius: 14px;
  background: #f3f8fe;
}

.chemical-pagination button {
  min-height: 36px;
  padding: 8px 13px;
  border-radius: 10px;
  border: none;
  color: white;
  background: var(--mg-blue);
  cursor: pointer;
  font-weight: 900;
}

.chemical-pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.selected-drugs-modal,
.research-face-modal-backdrop,
.studio-picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(3, 6, 12, 0.62);
  backdrop-filter: blur(14px);
}

.selected-drugs-card,
.research-face-modal,
.studio-picker-modal {
  width: min(980px, 96vw);
  max-height: 90vh;
  overflow: auto;
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid rgba(205, 220, 236, 0.9);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.30);
  padding: 22px;
}

.research-face-modal {
  width: min(760px, 96vw);
  padding: 0;
  background:
    radial-gradient(circle at 90% 0%, rgba(24, 183, 255, 0.08), transparent 32%),
    #ffffff;
}

.research-face-modal-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  padding: 20px;
  border-bottom: 1px solid var(--mg-border);
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.12), transparent 36%),
    linear-gradient(145deg, #03060c, #081525);
  color: white;
  border-radius: 24px 24px 0 0;
}

.research-face-modal-head span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.research-face-modal-head strong {
  display: block;
  color: white;
  font-size: 24px;
  font-weight: 950;
  letter-spacing: -0.04em;
}

.research-face-modal-head p {
  margin: 8px 0 0;
  color: #a9bdd3;
  line-height: 1.55;
}

.research-face-modal-head button,
.selected-drugs-head button,
.studio-picker-head button {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.18);
  color: white;
  background: rgba(255,255,255,0.10);
  cursor: pointer;
  font-size: 18px;
  font-weight: 950;
}

.research-face-modal .face-capture-panel,
.research-face-modal > div:not(.research-face-modal-head):not(.research-face-modal-actions) {
  margin: 18px;
}

.research-face-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px 20px;
  border-top: 1px solid #d9e8f6;
}

.selected-drugs-head,
.studio-picker-head,
.compound-designer-head,
.organ-impact-table-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.selected-drug-list {
  display: grid;
  gap: 10px;
}

.selected-drug-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px auto;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 14px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.selected-dose-edit {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px;
  align-items: center;
}

.selected-dose-edit input {
  min-height: 36px;
  padding: 8px;
  border: 1px solid var(--mg-border);
  border-radius: 10px;
}

.remove-selected-drug {
  min-height: 36px;
  border: 1px solid rgba(239, 68, 68, 0.22);
  background: rgba(239, 68, 68, 0.08);
  color: #991b1b;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 900;
}

.selected-drugs-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.patient-context-pro h2 {
  font-size: 20px;
}

.patient-context-pro .research-primary {
  width: 100%;
  margin-top: 12px;
}

.biometric-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.research-face-summary-card {
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.12), transparent 34%),
    linear-gradient(180deg, #ffffff, #f4f9ff);
}

.research-face-kicker {
  display: block;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.research-face-summary-bottom {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.research-face-summary-bottom span {
  color: var(--mg-muted);
  font-size: 12px;
  font-weight: 850;
}

.research-face-summary-bottom b {
  color: var(--mg-text);
}

.research-face-open-btn {
  width: 100%;
}

.bio-lab-library-page,
.bio-studio-page {
  display: grid;
  gap: 18px;
}

.bio-studio-shell {
  padding: 20px;
}

.bio-studio-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 160px;
  gap: 16px;
  align-items: stretch;
  margin-bottom: 14px;
  padding: 22px;
  border-radius: 20px;
  border: 1px solid #d9e8f6;
  background:
    radial-gradient(circle at 92% 0%, rgba(24,183,255,0.10), transparent 30%),
    #ffffff;
}

.bio-studio-hero h2 {
  margin: 0;
  font-size: clamp(24px, 3vw, 36px);
  line-height: 1.05;
  letter-spacing: -0.05em;
}

.bio-studio-hero span {
  display: block;
  margin-top: 8px;
  color: var(--mg-muted);
  line-height: 1.55;
}

.bio-studio-metrics {
  padding: 18px;
  border-radius: 16px;
  color: white;
  background: var(--mg-navy);
  display: grid;
  align-content: center;
  gap: 4px;
}

.bio-studio-metrics strong {
  font-size: 26px;
  color: #9ff7ff;
}

.bio-studio-metrics span {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
}

.bio-library-tabs,
.bio-workbench-mode-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0;
}

.avatar-test-queue-strip {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: center;
  padding: 15px;
  margin: 12px 0;
  border-radius: 16px;
  color: white;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.18), transparent 34%),
    linear-gradient(135deg, #03060c, #081525);
}

.avatar-test-queue-strip strong {
  display: block;
  color: white;
  font-size: 15px;
}

.avatar-test-queue-strip span {
  color: #a9bdd3;
  font-size: 12px;
}

.avatar-test-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.avatar-test-actions button {
  background: rgba(255,255,255,0.96);
}

.studio-status-banner {
  padding: 12px 14px;
  border-radius: 14px;
  margin: 12px 0;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-weight: 850;
}

.studio-elements-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 250px;
  gap: 14px;
  align-items: start;
}

.studio-side-help {
  padding: 16px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.bio-library-layout {
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 14px;
}

.bio-library-list {
  max-height: 680px;
  overflow: auto;
  display: grid;
  gap: 8px;
}

.bio-library-item {
  min-height: 72px;
}

.studio-molecule-preview {
  min-width: 0;
  display: grid;
  gap: 12px;
}

.studio-preview-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.studio-preview-actions button {
  min-height: 38px;
  padding: 8px 13px;
  border-radius: 10px;
  border: 1px solid var(--mg-border);
  background: #f8fbff;
  cursor: pointer;
  font-weight: 900;
}

.studio-preview-actions button.primary {
  color: white;
  background: var(--mg-blue);
  border-color: transparent;
}

.bio-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(165px, 1fr));
  gap: 12px;
}

.bio-select-card {
  min-height: 145px;
  background: #ffffff;
}

.bio-select-card small {
  display: block;
  margin-top: 8px;
  color: var(--mg-muted);
  line-height: 1.35;
}

.avatar-queue-card {
  padding: 18px;
  border-radius: 20px;
  border: 1px solid var(--mg-border);
  background: #ffffff;
}

.empty-studio-state {
  padding: 18px;
  border-radius: 16px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.empty-studio-state strong {
  display: block;
  margin-bottom: 6px;
}

.empty-studio-state span {
  color: var(--mg-muted);
}

.queue-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 12px;
}

.queue-card {
  cursor: default;
}

.queue-card button {
  margin-top: 10px;
  min-height: 34px;
  padding: 7px 11px;
  border-radius: 9px;
  border: 1px solid rgba(239, 68, 68, 0.22);
  color: #991b1b;
  background: rgba(239, 68, 68, 0.08);
  cursor: pointer;
  font-weight: 900;
}

.result-card-pro {
  overflow: hidden;
}

.risk-pill {
  padding: 8px 13px;
  border-radius: 999px;
  color: white;
  background: #64748b;
  text-transform: capitalize;
  font-weight: 950;
}

.risk-pill.high {
  background: var(--mg-red);
}

.risk-pill.moderate {
  background: var(--mg-yellow);
}

.risk-pill.low {
  background: var(--mg-green);
}

.score-meter {
  height: 10px;
  overflow: hidden;
  margin: 14px 0;
  border-radius: 999px;
  background: #dfeaf6;
}

.score-meter div {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--mg-cyan), var(--mg-blue));
}

.heatmap-layout-pro {
  display: grid;
  grid-template-columns: minmax(340px, 0.95fr) minmax(0, 1.05fr);
  gap: 14px;
  align-items: stretch;
}

.organ-impact-table-card {
  margin-top: 16px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
  background: #f8fbff;
}

.organ-impact-table-card h3 {
  margin: 0;
}

.organ-impact-table-card p {
  margin: 6px 0 0;
}

.organ-impact-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.organ-impact-table,
.structured-impact-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

.organ-impact-table th,
.organ-impact-table td,
.structured-impact-table th,
.structured-impact-table td {
  padding: 12px;
  border-bottom: 1px solid #d9e8f6;
  text-align: left;
  vertical-align: top;
}

.organ-impact-table th,
.structured-impact-table th {
  color: #125a94;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.organ-cell-name {
  display: flex;
  gap: 10px;
  align-items: center;
}

.organ-cell-name > span {
  width: 11px;
  height: 11px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.organ-cell-name strong {
  display: block;
  color: var(--mg-text);
}

.organ-cell-name small,
.organ-row-reason,
.organ-inline-meta {
  color: var(--mg-muted);
}

.organ-risk-chip {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 999px;
  border: 1px solid;
  font-size: 11px;
  font-weight: 950;
  text-transform: capitalize;
}

.organ-score-cell {
  display: grid;
  grid-template-columns: minmax(80px, 1fr) 42px;
  gap: 8px;
  align-items: center;
}

.organ-score-track {
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #dfeaf6;
}

.organ-score-track i {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.impact-workbench {
  display: grid;
  gap: 16px;
}

.impact-workbench-header {
  padding: 22px;
  border-radius: 22px;
  background: #ffffff;
  border: 1px solid var(--mg-border);
  box-shadow: 0 16px 44px rgba(7, 17, 31, 0.06);
}

.impact-workbench-header h2 {
  margin: 0;
  font-size: 28px;
  letter-spacing: -0.045em;
}

.impact-step-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.impact-validate-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 16px;
  align-items: start;
}

.impact-pro-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.impact-field.wide {
  grid-column: 1 / -1;
}

.textarea {
  min-height: 92px;
  resize: vertical;
}

.impact-action-row {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 14px;
}

.medgen-side-panel {
  padding: 20px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.18), transparent 34%),
    linear-gradient(145deg, #03060c, #081525);
  border: 1px solid rgba(24,183,255,0.18);
}

.medgen-side-panel h3 {
  color: white;
}

.medgen-side-panel p {
  color: #a9bdd3;
}

.medgen-stream-box,
.medgen-final-box {
  margin-top: 12px;
  padding: 14px;
  border-radius: 14px;
  background: rgba(255,255,255,0.075);
  border: 1px solid rgba(24,183,255,0.13);
  color: #dbeafe;
}

.medgen-stream-box pre,
.medgen-final-box pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}

.structured-impact-table-box {
  margin-top: 12px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(255,255,255,0.96);
  color: var(--mg-text);
}

.impact-table-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.impact-table-head button {
  min-height: 32px;
  padding: 7px 10px;
  border-radius: 9px;
  border: 1px solid var(--mg-border);
  background: #f8fbff;
  cursor: pointer;
  font-weight: 900;
}

.history-grid-pro {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.history-tile {
  border: 1px solid var(--mg-border);
}

.history-tile em {
  display: inline-flex;
  width: fit-content;
  margin-top: 10px;
  padding: 5px 9px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31,123,193,0.10);
  font-style: normal;
  font-size: 11px;
  font-weight: 900;
}

.research-empty {
  padding: 18px;
  border-radius: 16px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
  color: var(--mg-muted);
  font-weight: 850;
}

@media (max-width: 1350px) {
  .research-page {
    padding: 22px;
  }

  .research-layout-row {
    grid-template-columns: minmax(0, 1fr) 320px;
  }

  .chemical-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }

  .heatmap-layout-pro {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1120px) {
  .research-hero,
  .research-layout-row,
  .impact-validate-layout,
  .bio-studio-hero,
  .bio-library-layout,
  .studio-elements-layout {
    grid-template-columns: 1fr;
  }

  .research-warning,
  .bio-studio-metrics {
    max-width: 520px;
  }

  .sticky-panel {
    position: static;
    max-height: none;
  }

  .research-right {
    order: -1;
  }
}

@media (max-width: 760px) {
  .research-page {
    padding: 14px;
  }

  .research-hero,
  .research-card {
    padding: 16px;
    border-radius: 18px;
  }

  .research-hero h1 {
    font-size: 30px;
  }

  .research-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .research-tabs button {
    width: 100%;
  }

  .chemical-search-bar,
  .bio-library-search,
  .assist-search-row {
    grid-template-columns: 1fr;
  }

  .chemical-az-filter {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
  }

  .chemical-grid,
  .bio-card-grid,
  .queue-grid,
  .history-grid-pro {
    grid-template-columns: 1fr;
  }

  .research-card-head,
  .selected-drugs-head,
  .compound-designer-head,
  .organ-impact-table-head,
  .avatar-test-queue-strip {
    flex-direction: column;
  }

  .selected-drug-item {
    grid-template-columns: 1fr;
  }

  .biometric-row,
  .impact-pro-grid {
    grid-template-columns: 1fr;
  }

  .impact-field.wide {
    grid-column: auto;
  }

  .impact-action-row,
  .selected-drugs-actions,
  .research-face-modal-actions,
  .avatar-test-actions {
    flex-direction: column;
  }

  .impact-action-row button,
  .selected-drugs-actions button,
  .research-face-modal-actions button,
  .avatar-test-actions button,
  .research-primary,
  .impact-secondary-btn {
    width: 100%;
  }

  .research-face-modal,
  .selected-drugs-card {
    width: 96vw;
    max-height: 92vh;
    border-radius: 20px;
  }

  .research-face-modal-head {
    border-radius: 20px 20px 0 0;
  }
}
`;

export default function ResearchPage() {
  const [patients, setPatients] = useState([]);
  const [chemicals, setChemicals] = useState([]);

  const [chemicalSearch, setChemicalSearch] = useState("");
  const [chemicalPage, setChemicalPage] = useState(1);
  const [chemicalPages, setChemicalPages] = useState(1);
  const [chemicalTotal, setChemicalTotal] = useState(0);
  const [chemicalPageSize] = useState(40);
  const [chemicalLetter, setChemicalLetter] = useState("");
  const [selectedPanelOpen, setSelectedPanelOpen] = useState(false);

  const [selectedChemicals, setSelectedChemicals] = useState([]);
  const [doseMap, setDoseMap] = useState({});
  const [patientId, setPatientId] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);

  const [newChemical, setNewChemical] = useState(emptyChemical);
  const [newImpact, setNewImpact] = useState(emptyImpact);

  const [activeTab, setActiveTab] = useState("simulate");
  const [loading, setLoading] = useState(false);

  const [gender, setGender] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [weightKg, setWeightKg] = useState("");

  const [assistQuery, setAssistQuery] = useState("");
  const [assistResult, setAssistResult] = useState(null);
  const [assistLoading, setAssistLoading] = useState(false);

  const [askLlmLoading, setAskLlmLoading] = useState(false);
  const [llmSuggestion, setLlmSuggestion] = useState(null);
  const [medgenStreamText, setMedgenStreamText] = useState("");

  const [impactStep, setImpactStep] = useState("find");
  const [impactRows, setImpactRows] = useState([]);
  const [patientResearchContext, setPatientResearchContext] = useState(null);

  const [patientSessions, setPatientSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedSessionContext, setSelectedSessionContext] = useState(null);

  const [bioElements, setBioElements] = useState([]);
  const [bioCompounds, setBioCompounds] = useState([]);
  const [bioProteins, setBioProteins] = useState([]);
  const [bioMedicines, setBioMedicines] = useState([]);

  const [bioLibraryMode, setBioLibraryMode] = useState("elements");
  const [bioSearch, setBioSearch] = useState("");

  const [selectedBioElements, setSelectedBioElements] = useState([]);
  const [selectedBioCompound, setSelectedBioCompound] = useState(null);
  const [selectedBioCompoundDetail, setSelectedBioCompoundDetail] = useState(null);
  const [selectedBioProteins, setSelectedBioProteins] = useState([]);
  const [selectedBioMedicines, setSelectedBioMedicines] = useState([]);

  const [avatarFaceProfile, setAvatarFaceProfile] = useState(null);
  const [faceScannerOpen, setFaceScannerOpen] = useState(false);

  const [selectedBioCompounds, setSelectedBioCompounds] = useState([]);
  const [activeBioWorkbench, setActiveBioWorkbench] = useState("explore");

  const [compoundDesigner, setCompoundDesigner] = useState({
    base_compound_id: null,
    base_name: "",
    name: "",
    formula: "",
    compound_type: "designed_biomolecule",
    atoms: [],
    bonds: [],
    notes: "",
  });

  const [simulationSaveStatus, setSimulationSaveStatus] = useState("");

  useEffect(() => {
    loadPatients();
    loadChemicals(1, "", "");
    loadHistory();
    loadBioElements();
    loadBioCompounds();
    loadBioProteins();
    loadBioMedicines();
  }, []);

  const selectedPatient = useMemo(
    () => patients.find((p) => String(p.id) === String(patientId)),
    [patients, patientId]
  );

  const selectedChemicalsWithDose = useMemo(
    () =>
      selectedChemicals.map((c) => ({
        ...c,
        dose: Number(doseMap[c.id] || 100),
        selectedDose: Number(doseMap[c.id] || 100),
        unit: "mg",
      })),
    [selectedChemicals, doseMap]
  );

  const activeResult = selectedHistory?.result_payload || result;

  const overallScore = useMemo(() => {
    if (!activeResult?.organs) return 0;
    const values = Object.values(activeResult.organs).map((x) =>
      Number(x.score || 0)
    );
    return values.length ? Math.max(...values) : 0;
  }, [activeResult]);

  const calculatedBmi = useMemo(() => {
    if (!heightCm || !weightKg) return "";
    const h = Number(heightCm) / 100;
    const w = Number(weightKg);
    if (!h || !w) return "";
    return (w / (h * h)).toFixed(2);
  }, [heightCm, weightKg]);

  const loadPatients = async () => {
    try {
      const res = await api.get("/api/patient/");
      setPatients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load patients", err);
      setPatients([]);
    }
  };

  const loadPatientSessions = async (pid) => {
    if (!pid) {
      setPatientSessions([]);
      setSelectedSessionId("");
      setSelectedSessionContext(null);
      return;
    }

    const possibleUrls = [
      `/api/session/patient/${pid}`,
      `/api/session/?patient_id=${pid}`,
      `/api/sessions?patient_id=${pid}`,
    ];

    for (const url of possibleUrls) {
      try {
        const res = await api.get(url);
        const rows = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.items)
            ? res.data.items
            : [];

        setPatientSessions(rows);
        return;
      } catch {
        // try next endpoint
      }
    }

    console.warn("No patient session endpoint matched for patient:", pid);
    setPatientSessions([]);
  };

  const loadChemicals = async (
    page = 1,
    q = chemicalSearch,
    letter = chemicalLetter
  ) => {
    try {
      const res = await api.get("/api/research/chemicals", {
        params: {
          q: q || undefined,
          letter: letter || undefined,
          page,
          page_size: chemicalPageSize,
        },
      });

      const payload = res.data;

      if (Array.isArray(payload)) {
        setChemicals(payload);
        setChemicalPage(1);
        setChemicalPages(1);
        setChemicalTotal(payload.length);
        return;
      }

      setChemicals(Array.isArray(payload?.items) ? payload.items : []);
      setChemicalPage(payload?.page || page);
      setChemicalPages(payload?.pages || 1);
      setChemicalTotal(payload?.total || 0);
    } catch (err) {
      console.error("Failed to load chemicals", err);
      setChemicals([]);
    }
  };

  const filterByLetter = async (letter) => {
    setChemicalLetter(letter);
    setChemicalSearch("");
    setChemicalPage(1);
    await loadChemicals(1, "", letter);
  };

  const searchChemicals = async () => {
    setChemicalLetter("");
    setChemicalPage(1);
    await loadChemicals(1, chemicalSearch, "");
  };

  const clearChemicalSearch = async () => {
    setChemicalSearch("");
    setChemicalLetter("");
    setChemicalPage(1);
    await loadChemicals(1, "", "");
  };

  const goChemicalPage = async (nextPage) => {
    const safePage = Math.min(Math.max(nextPage, 1), chemicalPages);
    setChemicalPage(safePage);
    await loadChemicals(safePage, chemicalSearch, chemicalLetter);
  };

  const loadHistory = async () => {
    try {
      const [legacyRes, bioRes] = await Promise.allSettled([
        api.get("/api/research/simulations"),
        api.get("/api/research/bio/simulations"),
      ]);

      const legacyRows =
        legacyRes.status === "fulfilled" && Array.isArray(legacyRes.value.data)
          ? legacyRes.value.data
          : [];

      const bioRaw =
        bioRes.status === "fulfilled"
          ? Array.isArray(bioRes.value.data)
            ? bioRes.value.data
            : bioRes.value.data?.items || []
          : [];

      const bioRows = bioRaw.map((row) => ({
        ...row,
        title: row.title || "Digital Avatar Bio Simulation",
        result_payload: row.predicted_impact || row.result_payload || {},
        source: "bio_simulation_runs",
        created_at: row.created_at || row.createdAt || null,
      }));

      const merged = [...legacyRows, ...bioRows].sort((a, b) => {
        const da = new Date(a.created_at || a.createdAt || 0).getTime();
        const db = new Date(b.created_at || b.createdAt || 0).getTime();
        return db - da;
      });

      setHistory(merged);
    } catch (err) {
      console.error("Failed to load simulation history", err);
      setHistory([]);
    }
  };

  const loadBioElements = async (q = "") => {
    try {
      const res = await api.get("/api/research/bio/elements", {
        params: { q: q || undefined },
      });
      setBioElements(res.data?.items || []);
    } catch (err) {
      console.error("Failed to load bio elements", err);
      setBioElements([]);
    }
  };

  const loadBioCompounds = async (q = "") => {
    try {
      const res = await api.get("/api/research/bio/compounds", {
        params: {
          q: q || undefined,
          limit: 100,
        },
      });
      setBioCompounds(res.data?.items || []);
    } catch (err) {
      console.error("Failed to load bio compounds", err);
      setBioCompounds([]);
    }
  };

  const loadBioProteins = async (q = "") => {
    try {
      const res = await api.get("/api/research/bio/proteins", {
        params: {
          q: q || undefined,
          limit: 100,
        },
      });
      setBioProteins(res.data?.items || []);
    } catch (err) {
      console.error("Failed to load bio proteins", err);
      setBioProteins([]);
    }
  };

  const loadBioMedicines = async (q = "") => {
    try {
      const res = await api.get("/api/research/bio/medicines", {
        params: {
          q: q || undefined,
          limit: 100,
        },
      });
      setBioMedicines(res.data?.items || []);
    } catch (err) {
      console.error("Failed to load bio medicines", err);
      setBioMedicines([]);
    }
  };

  const loadBioCompoundDetail = async (compoundId) => {
    if (!compoundId) return;

    try {
      const res = await api.get(`/api/research/bio/compounds/${compoundId}`);
      setSelectedBioCompoundDetail(res.data);
      setSelectedBioCompound(res.data?.compound || null);
    } catch (err) {
      console.error("Failed to load bio compound detail", err);
      setSelectedBioCompoundDetail(null);
    }
  };

  const searchBioLibrary = async () => {
    if (bioLibraryMode === "elements") await loadBioElements(bioSearch);
    if (bioLibraryMode === "compounds") await loadBioCompounds(bioSearch);
    if (bioLibraryMode === "proteins") await loadBioProteins(bioSearch);
    if (bioLibraryMode === "medicines") await loadBioMedicines(bioSearch);
  };

  const toggleBioElement = (element) => {
    setSelectedBioElements((prev) => {
      const exists = prev.some((item) => item.id === element.id);
      if (exists) return prev.filter((item) => item.id !== element.id);
      return [...prev, element];
    });
  };

  const toggleBioProtein = (protein) => {
    setSelectedBioProteins((prev) => {
      const exists = prev.some((item) => item.id === protein.id);
      if (exists) return prev.filter((item) => item.id !== protein.id);
      return [...prev, protein];
    });
  };

  const toggleBioMedicine = (medicine) => {
    setSelectedBioMedicines((prev) => {
      const exists = prev.some((item) => item.id === medicine.id);
      if (exists) return prev.filter((item) => item.id !== medicine.id);
      return [...prev, medicine];
    });
  };

  const loadBioMedicineDetail = async (medicine) => {
    try {
      const res = await api.get(`/api/research/bio/medicines/${medicine.id}`);

      const enrichedMedicine = {
        ...medicine,
        detail: res.data,
        targets: res.data?.targets || [],
        compounds: res.data?.compounds || [],
      };

      setSelectedBioMedicines((prev) => {
        const exists = prev.some((item) => item.id === medicine.id);
        if (exists) return prev.filter((item) => item.id !== medicine.id);
        return [...prev, enrichedMedicine];
      });
    } catch (err) {
      console.error("Failed to load medicine detail", err);
      toggleBioMedicine(medicine);
    }
  };

  const addBioCompoundToAvatar = (detail) => {
    const compound = detail?.compound || detail;
    if (!compound?.id) return;

    setSelectedBioCompounds((prev) => {
      const exists = prev.some((item) => item.compound?.id === compound.id);
      if (exists) return prev;

      return [
        ...prev,
        {
          compound,
          atoms: detail?.atoms || [],
          bonds: detail?.bonds || [],
          source: "bio_library",
        },
      ];
    });
  };

  const removeBioCompoundFromAvatar = (compoundId) => {
    setSelectedBioCompounds((prev) =>
      prev.filter((item) => item.compound?.id !== compoundId)
    );
  };

  const clearBioAvatarQueue = () => {
    setSelectedBioCompounds([]);
    setSelectedBioElements([]);
    setSelectedBioProteins([]);
    setSelectedBioMedicines([]);
  };

  const startBlankCompoundStudio = () => {
    setCompoundDesigner({
      base_compound_id: null,
      base_name: "",
      name: `Designed Compound ${new Date().toLocaleTimeString()}`,
      formula: "",
      compound_type: "designed_biomolecule",
      atoms: [],
      bonds: [],
      notes:
        "Researcher-designed compound. Bonds and atoms are visual-only until saved/validated.",
    });

    setActiveBioWorkbench("studio");
  };

  const openSelectedCompoundInStudio = () => {
    if (!selectedBioCompoundDetail?.compound) {
      alert("Select a biomolecule/compound first, then open Studio.");
      return;
    }

    const base = selectedBioCompoundDetail.compound;

    setCompoundDesigner({
      base_compound_id: base.id,
      base_name: base.name,
      name: `${base.name} - Designed Variant`,
      formula: base.display_formula || base.formula || "",
      compound_type: "designed_biomolecule",
      atoms: (selectedBioCompoundDetail.atoms || []).map((atom, index) => ({
        ...atom,
        atom_label: atom.atom_label || `${atom.element_symbol || "X"}${index + 1}`,
        element_symbol: atom.element_symbol || "X",
        position_x: Number(atom.position_x || index * 0.2),
        position_y: Number(atom.position_y || 0),
        position_z: Number(atom.position_z || 0),
      })),
      bonds: selectedBioCompoundDetail.bonds || [],
      notes: "Designed from existing Bio Lab compound.",
    });

    setActiveBioWorkbench("studio");
  };

  const addDesignedCompoundToAvatar = () => {
    if (!compoundDesigner.name || !compoundDesigner.atoms.length) {
      alert("Create a designed compound with at least one atom.");
      return;
    }

    const designedCompound = {
      id: `designed-${Date.now()}`,
      name: compoundDesigner.name,
      formula: compoundDesigner.formula,
      display_formula: compoundDesigner.formula,
      compound_type: compoundDesigner.compound_type,
      description: compoundDesigner.notes,
      is_designed: true,
    };

    setSelectedBioCompounds((prev) => [
      ...prev,
      {
        compound: designedCompound,
        atoms: compoundDesigner.atoms,
        bonds: compoundDesigner.bonds,
        source: "researcher_designed",
      },
    ]);

    setSelectedBioCompound(designedCompound);
    setSelectedBioCompoundDetail({
      compound: designedCompound,
      atoms: compoundDesigner.atoms,
      bonds: compoundDesigner.bonds,
    });

    setActiveBioWorkbench("queue");
  };

  const buildFormulaFromAtoms = (atoms = []) => {
    const counts = {};

    atoms.forEach((atom) => {
      const symbol = atom.element_symbol || "X";
      counts[symbol] = (counts[symbol] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([symbol, count]) => `${symbol}${count > 1 ? count : ""}`)
      .join("");
  };

  const createChemical = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...newChemical,
        molecular_weight: newChemical.molecular_weight
          ? Number(newChemical.molecular_weight)
          : null,
      };

      await api.post("/api/research/chemicals", payload);
      setNewChemical(emptyChemical);
      await loadChemicals(chemicalPage, chemicalSearch, chemicalLetter);
      alert("Chemical added");
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to create chemical");
    }
  };

  const createImpact = async (e) => {
    e.preventDefault();

    if (!newImpact.chemical_id) {
      alert("Select chemical first");
      return;
    }

    try {
      await api.post("/api/research/impacts", {
        ...newImpact,
        chemical_id: Number(newImpact.chemical_id),
        severity: Number(newImpact.severity),
      });

      setNewImpact(emptyImpact);
      alert("Impact mapping added");
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to create impact mapping");
    }
  };

  const toggleChemical = (chem) => {
    if (selectedChemicals.find((c) => c.id === chem.id)) {
      setSelectedChemicals((prev) => prev.filter((c) => c.id !== chem.id));
    } else {
      setSelectedChemicals((prev) => [...prev, chem]);
      setDoseMap((prev) => ({ ...prev, [chem.id]: prev[chem.id] || 100 }));
    }
  };

  const handlePatientChange = (value) => {
    setPatientId(value);
    setSelectedSessionId("");
    setSelectedSessionContext(null);

    const patient = patients.find((p) => String(p.id) === String(value));
    if (patient?.gender) {
      setGender(String(patient.gender).toLowerCase());
    }

    loadPatientSessions(value);
  };

  const runSimulation = async () => {
    const hasLegacyChemicals = selectedChemicals.length > 0;
    const hasBioSelection =
      selectedBioElements.length > 0 ||
      Boolean(selectedBioCompoundDetail) ||
      selectedBioCompounds.length > 0 ||
      selectedBioProteins.length > 0 ||
      selectedBioMedicines.length > 0;
    const hasAvatarProfile = Boolean(avatarFaceProfile);

    if (!hasLegacyChemicals && !hasBioSelection && !hasAvatarProfile) {
      alert(
        "Select at least one chemical, compound, protein, medicine, element, or attach an avatar face profile."
      );
      return;
    }

    setLoading(true);
    setResult(null);
    setSelectedHistory(null);

    try {
      const hasPatientContext =
        patientId || gender || heightCm || weightKg || selectedSessionId;

      const payload = {
        title: `Simulation - ${new Date().toLocaleString()}`,

        chemicals: selectedChemicals.map((c) => ({
          chemical_id: c.id,
          name: c.name,
          formula: c.formula,
          category: c.category,
          dose: Number(doseMap[c.id] || 100),
          unit: "mg",
        })),

        patient: hasPatientContext
          ? {
              patient_id: patientId ? Number(patientId) : null,
              gender: gender || null,
              height_cm: heightCm ? Number(heightCm) : null,
              weight_kg: weightKg ? Number(weightKg) : null,
              bmi: calculatedBmi ? Number(calculatedBmi) : null,
              patient_session_id: selectedSessionId
                ? Number(selectedSessionId)
                : null,
              patient_session_context: selectedSessionContext || null,
              consent_given: true,
            }
          : null,

        research_context: {
          ...(patientResearchContext || {}),
          bio_lab: {
            selected_elements: selectedBioElements,
            selected_compound_detail: selectedBioCompoundDetail,
            selected_proteins: selectedBioProteins,
            selected_medicines: selectedBioMedicines,
            patient_session_id: selectedSessionId ? Number(selectedSessionId) : null,
            patient_session_context: selectedSessionContext || null,
          },
          avatar_face_profile_available: hasAvatarProfile,
        },
      };

      let res = { data: null };

      if (hasLegacyChemicals) {
        res = await api.post("/api/research/simulate", payload);
        setResult(res.data);
      } else {
        res = {
          data: {
            overall_risk: "unknown",
            organs: {},
            explanation:
              "Digital avatar simulation saved. Legacy organ impact engine was skipped because no legacy chemical was selected.",
          },
        };
        setResult(res.data);
      }

      try {
        await api.post("/api/research/bio/simulations", {
          patient_id: patientId ? Number(patientId) : null,
          title: `Digital Avatar Bio Simulation - ${new Date().toLocaleString()}`,
          simulation_type: "digital_avatar_lab",

          selected_elements: selectedBioElements.map((item) => ({
            id: item.id,
            symbol: item.symbol,
            name: item.name,
            atomic_number: item.atomic_number,
            category: item.category,
          })),

          selected_compounds:
            selectedBioCompounds.length > 0
              ? selectedBioCompounds.map((item) => ({
                  compound: item.compound,
                  atoms: item.atoms || [],
                  bonds: item.bonds || [],
                  source: item.source || "bio_library",
                }))
              : selectedBioCompoundDetail
                ? [
                    {
                      compound: selectedBioCompoundDetail.compound,
                      atoms: selectedBioCompoundDetail.atoms || [],
                      bonds: selectedBioCompoundDetail.bonds || [],
                      source: "active_preview",
                    },
                  ]
                : [],

          selected_proteins: selectedBioProteins.map((item) => ({
            id: item.id,
            symbol: item.symbol,
            name: item.name,
            protein_type: item.protein_type,
            system: item.system,
            primary_organ: item.primary_organ,
          })),

          selected_medicines: selectedBioMedicines.map((item) => ({
            id: item.id,
            name: item.name,
            generic_name: item.generic_name,
            category: item.category,
            formula: item.formula,
            affected_systems: item.affected_systems,
            affected_organs: item.affected_organs,
            targets: item.targets || [],
            compounds: item.compounds || [],
          })),

          avatar_snapshot: {
            gender: gender || null,
            height_cm: heightCm ? Number(heightCm) : null,
            weight_kg: weightKg ? Number(weightKg) : null,
            bmi: calculatedBmi ? Number(calculatedBmi) : null,
            patient_id: patientId ? Number(patientId) : null,
            patient_session_id: selectedSessionId ? Number(selectedSessionId) : null,
            patient_session_context: selectedSessionContext || null,
            face_profile_attached: hasAvatarProfile,

            face_mask_profile: avatarFaceProfile
              ? {
                  descriptor_preview:
                    avatarFaceProfile.descriptor_preview || null,
                  capture_quality: avatarFaceProfile.capture_quality || null,
                  created_at: avatarFaceProfile.created_at || null,
                  source: avatarFaceProfile.source || "digital_face_scanner",
                  research_only: true,
                  diagnostic_use: false,
                  eye_profile: avatarFaceProfile.eye_profile || null,
                  eye_analysis: avatarFaceProfile.eye_analysis || null,
                  hra_model_note:
                    avatarFaceProfile.hra_model_note ||
                    "Current HRA model has anatomical skin mesh without face UV texture mapping. Face scan is stored as avatar mask/reference layer.",
                }
              : null,

            reason:
              "No human, animal, or living organism is tested. This is a research-only digital avatar simulation using known or hypothesized relationships between chemicals, drugs, proteins, enzymes, hormones, organs, patient context, and avatar face reference profile.",
          },

          predicted_impact: res.data || {},
          risk_level: res.data?.overall_risk || null,
          disclaimer_acknowledged: true,
          notes:
            "Research-only digital avatar simulation. Face capture is used only as avatar identity/mask reference, not diagnosis and not clinical decision-making.",
        });
      } catch (bioErr) {
        console.warn("Bio simulation save failed", bioErr);
      }

      await loadHistory();
    } catch (err) {
      alert(err?.response?.data?.detail || "Simulation failed");
    } finally {
      setLoading(false);
    }
  };

  const openHistoryResult = (sim) => {
    setSelectedHistory(sim);
    setResult(null);
    setActiveTab("history");

    setTimeout(() => {
      const el = document.getElementById("history-result-view");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const downloadCsv = (rows, filename) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      alert("No rows available for CSV export.");
      return;
    }

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((key) => `"${String(row[key] ?? "").replaceAll('"', '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  };

  const resultOrgansToRows = (simulationResult = activeResult) =>
    Object.entries(simulationResult?.organs || {}).map(([organ, data]) => ({
      organ: formatInlineOrgan(organ),
      organ_key: organ,
      risk: data?.risk || scoreToRiskLocal(Number(data?.score || 0)),
      score: Number(data?.score || 0),
      reason: Array.isArray(data?.reasons) ? data.reasons.join(" | ") : "",
    }));

  const exportResultOrgansCsv = (simulationResult = activeResult) => {
    downloadCsv(resultOrgansToRows(simulationResult), "digital-avatar-organ-impact.csv");
  };

  const scoreToRiskLocal = (score) => {
    if (score < 0.3) return "low";
    if (score < 0.6) return "moderate";
    return "high";
  };

  const getOverallRiskLocal = (organs) => {
    const values = Object.values(organs || {}).map((x) => Number(x.score || 0));
    const max = values.length ? Math.max(...values) : 0;
    return scoreToRiskLocal(max);
  };

  const mergeMedgenImpactsIntoResult = (rows = []) => {
    const cleanRows = normalizeMedgenRows(rows);
    if (!cleanRows.length) return;

    setResult((prev) => {
      const base = prev || selectedHistory?.result_payload || {};
      const existingOrgans = base.organs || {};
      const updatedOrgans = { ...existingOrgans };

      cleanRows.forEach((row) => {
        const organ = normalizeOrgan(row.organ);
        if (!organ) return;

        const score = clamp01(Number(row.severity ?? row.score ?? 0.4));

        updatedOrgans[organ] = {
          score,
          risk: scoreToRiskLocal(score),
          system: row.system || updatedOrgans[organ]?.system || "",
          impact_type: row.impact_type || updatedOrgans[organ]?.impact_type || "impact",
          confidence: row.confidence ?? updatedOrgans[organ]?.confidence ?? 0.5,
          mechanism: row.mechanism || updatedOrgans[organ]?.mechanism || "",
          reasons: [
            row.description ||
              row.mechanism ||
              `${row.impact_type || "impact"} impact suggested by Medgen.`,
          ],
        };
      });

      return {
        ...base,
        organs: updatedOrgans,
        overall_risk: getOverallRiskLocal(updatedOrgans),
      };
    });

    setSelectedHistory(null);
  };

  const compactForMedgen = (value, depth = 0) => {
    if (value === null || value === undefined) return value;

    if (typeof value === "string") {
      if (value.startsWith("data:image/")) {
        return "[image_data_removed_for_text_llm]";
      }

      if (value.length > 1200) {
        return `${value.slice(0, 1200)}... [truncated]`;
      }

      return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return value;
    }

    if (Array.isArray(value)) {
      if (depth > 3) return `[array_removed_depth_${depth}]`;
      return value.slice(0, 20).map((item) => compactForMedgen(item, depth + 1));
    }

    if (typeof value === "object") {
      if (depth > 3) return `[object_removed_depth_${depth}]`;

      const blockedKeys = new Set([
        "image_data_url",
        "face_image_base64",
        "capturedImage",
        "left_eye_data_url",
        "right_eye_data_url",
        "eye_crops",
        "landmarks_68",
        "atoms",
        "bonds",
        "raw",
        "transcript",
        "full_text",
        "fullBody",
        "body",
        "html",
        "base64",
      ]);

      const output = {};

      Object.entries(value).forEach(([key, item]) => {
        if (blockedKeys.has(key)) {
          output[key] = `[${key}_removed_for_text_llm]`;
        } else {
          output[key] = compactForMedgen(item, depth + 1);
        }
      });

      return output;
    }

    return String(value);
  };

  const askLlmForImpact = async () => {
    const selectedChemical = chemicals.find(
      (c) => String(c.id) === String(newImpact.chemical_id)
    );

    const chemicalName = selectedChemical?.name || newChemical.name || "";

    if (!chemicalName) {
      alert("Select or enter a chemical first");
      return;
    }

    setAskLlmLoading(true);
    setLlmSuggestion(null);
    setImpactRows([]);
    setMedgenStreamText("Connecting to Medgen...");

    try {
      const token = localStorage.getItem("token");

      const bmi =
        calculatedBmi ||
        (heightCm && weightKg
          ? (Number(weightKg) / ((Number(heightCm) / 100) ** 2)).toFixed(2)
          : null);

      const response = await fetch(
        `${api.defaults.baseURL}/api/research/ask-llm-impact/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(
            compactForMedgen({
              chemical_name: chemicalName,
              formula: selectedChemical?.formula || newChemical.formula || null,
              molecular_weight:
                selectedChemical?.molecular_weight ||
                (newChemical.molecular_weight
                  ? Number(newChemical.molecular_weight)
                  : null),
              category: selectedChemical?.category || newChemical.category || null,
              patient_gender: gender || null,
              height_cm: heightCm ? Number(heightCm) : null,
              weight_kg: weightKg ? Number(weightKg) : null,
              bmi: bmi ? Number(bmi) : null,
              notes: JSON.stringify(
                compactForMedgen({
                  researcher_note:
                    newImpact.description || "Research impact analysis",
                  patient_context: patientResearchContext
                    ? {
                        patient_id: patientId ? Number(patientId) : null,
                        patient_name: selectedPatient?.name || null,
                        gender: gender || selectedPatient?.gender || null,
                        summary:
                          patientResearchContext.summary ||
                          patientResearchContext.initial_summary ||
                          patientResearchContext.doctor_note ||
                          null,
                        red_flags:
                          patientResearchContext.red_flags ||
                          patientResearchContext.possible_concerns ||
                          null,
                        suggested_tests:
                          patientResearchContext.suggested_tests || null,
                        medicine_concerns:
                          patientResearchContext.medicine_concerns || null,
                      }
                    : null,
                  patient_session: selectedSessionId
                    ? {
                        patient_session_id: Number(selectedSessionId),
                        attached: true,
                        note:
                          "Patient session context is attached in compact form only. Full transcript/raw body removed for LLM stability.",
                      }
                    : null,
                  selected_drug_combination: selectedChemicalsWithDose.map((c) => ({
                    id: c.id,
                    name: c.name,
                    formula: c.formula,
                    category: c.category,
                    dose: c.dose,
                    unit: c.unit,
                  })),
                  bio_lab: {
                    selected_elements: selectedBioElements.map((item) => ({
                      symbol: item.symbol,
                      name: item.name,
                      category: item.category,
                    })),
                    selected_compound: selectedBioCompound
                      ? {
                          id: selectedBioCompound.id,
                          name: selectedBioCompound.name,
                          formula: selectedBioCompound.formula,
                          compound_type: selectedBioCompound.compound_type,
                          description: selectedBioCompound.description,
                        }
                      : null,
                    selected_proteins: selectedBioProteins.map((item) => ({
                      symbol: item.symbol,
                      name: item.name,
                      protein_type: item.protein_type,
                      system: item.system,
                      primary_organ: item.primary_organ,
                    })),
                    selected_medicines: selectedBioMedicines.map((item) => ({
                      id: item.id,
                      name: item.name,
                      generic_name: item.generic_name,
                      category: item.category,
                      formula: item.formula,
                      affected_systems: item.affected_systems,
                      affected_organs: item.affected_organs,
                    })),
                  },
                  instruction:
                    "Return structured organ impacts only. Backend will parse final JSON. No markdown. No long explanation. No diagnosis. No prescriptions. Digital avatar research simulation only.",
                }),
                null,
                2
              ),
            })
          ),
        }
      );

      if (!response.ok || !response.body) {
        throw new Error("Medgen stream failed");
      }

      await readMedgenStream(response, {
        onStart: () => {
          setMedgenStreamText("Starting Medgen analysis...");
        },

        onProgress: (message) => {
          setMedgenStreamText(message || "Medgen is working...");
        },

        onFinal: (data) => {
          const rows = normalizeMedgenRows(data?.impacts || []);

          if (!rows.length) {
            setImpactRows([]);
            setLlmSuggestion(data || null);
            setMedgenStreamText("No valid impact rows received.");
            setAskLlmLoading(false);
            return;
          }

          setImpactRows(rows);
          setLlmSuggestion(data);
          setMedgenStreamText("Structured impact table ready.");
          setAskLlmLoading(false);

          const selected = rows[0];

          setNewImpact((prev) => ({
            ...prev,
            organ: selected.organ || prev.organ,
            system: selected.system || prev.system,
            impact_type: selected.impact_type || prev.impact_type,
            severity:
              selected.severity !== undefined
                ? String(selected.severity)
                : prev.severity,
            description:
              selected.description || selected.mechanism || prev.description,
          }));
        },

        onDone: () => {
          setAskLlmLoading(false);
        },
      });
    } catch (err) {
      console.error("Ask Medgen failed", err);
      setMedgenStreamText("");
      alert(err?.message || "Ask Medgen failed");
    } finally {
      setAskLlmLoading(false);
    }
  };

  const assistedSearch = async () => {
    if (!assistQuery.trim()) {
      alert("Enter chemical name");
      return;
    }

    setAssistLoading(true);
    setAssistResult(null);

    try {
      const res = await api.get(
        `/api/research/chemicals/search/${encodeURIComponent(assistQuery)}`
      );

      setAssistResult(res.data);

      if (res.data?.found && res.data.chemical) {
        const chem = res.data.chemical;

        setNewChemical({
          name: chem.name || "",
          formula: chem.formula || "",
          molecular_weight: chem.molecular_weight || "",
          category: chem.category || "compound",
          description: chem.description || "",
        });

        setNewImpact((prev) => ({
          ...prev,
          chemical_id: chem.id,
        }));

        if (res.data.impact_suggestions?.length > 0) {
          const impact = res.data.impact_suggestions[0];

          setNewImpact({
            chemical_id: chem.id,
            organ: impact.organ || "liver",
            system: impact.system || "",
            impact_type: impact.impact_type || "stress",
            severity: String(impact.severity ?? "0.4"),
            description:
              impact.description || impact.mechanism || "Auto-filled suggestion",
          });
        }
      }
    } catch (err) {
      alert(err?.response?.data?.detail || "Assisted search failed");
    } finally {
      setAssistLoading(false);
    }
  };

  return (
    <div className="research-page">
      <style>{researchInlineCss}</style>

      <div className="research-hero research-hero-pro">
        <div>
          <div className="research-kicker">RESEARCH MODE</div>
          <h1>Pharmacology Simulation Lab</h1>
          <p>
            Model chemical-level organ impact using curated mappings,
            BMI-aware scoring, facial capture references, patient history, and
            drug-combination context.
          </p>

          <div className="research-mini-stats">
            <span>{chemicalTotal || chemicals.length} Legacy Chemicals</span>
            <span>{bioElements.length} Elements</span>
            <span>{bioCompounds.length} Bio Compounds</span>
            <span>{bioMedicines.length} Medicines</span>
            <span>{history.length} Simulations</span>
            <span>{patients.length} Patients</span>
          </div>
        </div>

        <div className="research-warning">
          <strong>Research Only</strong>
          <span>Not for diagnosis, prescription, or clinical decisions.</span>
        </div>
      </div>

      <div className="research-tabs">
        <button
          className={activeTab === "simulate" ? "active" : ""}
          onClick={() => setActiveTab("simulate")}
          type="button"
        >
          Simulation Builder
        </button>

        <button
          className={activeTab === "chemicals" ? "active" : ""}
          onClick={() => setActiveTab("chemicals")}
          type="button"
        >
          Chemical Library
        </button>

        <button
          className={activeTab === "impacts" ? "active" : ""}
          onClick={() => setActiveTab("impacts")}
          type="button"
        >
          Impact Mapping
        </button>

        <button
          className={activeTab === "history" ? "active" : ""}
          onClick={() => setActiveTab("history")}
          type="button"
        >
          History
        </button>
        <button
          className={activeTab === "dna" ? "active" : ""}
          onClick={() => setActiveTab("dna")}
        >
          DNA Studio
        </button>
      </div>

      {activeTab === "simulate" && (
        <div className="research-layout-row">
          <div className="research-left">
            <section className="research-card wide simulation-canvas-card compact-builder">
              <div className="research-card-head">
                <div>
                  <h2>Select Drug / Chemical Combination</h2>
                  <p>Choose one or more compounds and define experimental dose.</p>
                </div>

                <button
                  type="button"
                  className="selected-drugs-open-btn"
                  onClick={() => setSelectedPanelOpen(true)}
                >
                  {selectedChemicals.length} selected
                </button>
              </div>

              <div className="chemical-search-bar">
                <input
                  value={chemicalSearch}
                  placeholder="Search medicine, salt, combination..."
                  onChange={(e) => setChemicalSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchChemicals()}
                />

                <button type="button" onClick={searchChemicals}>
                  Search
                </button>

                <button
                  type="button"
                  className="secondary"
                  onClick={clearChemicalSearch}
                >
                  Clear
                </button>
              </div>

              <div className="chemical-az-filter">
                {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter) => (
                  <button
                    key={letter}
                    type="button"
                    className={chemicalLetter === letter ? "active" : ""}
                    onClick={() => filterByLetter(letter)}
                  >
                    {letter}
                  </button>
                ))}
              </div>

              <div className="chemical-pagination-info">
                <span>{chemicalTotal} medicines found</span>
                <span>
                  Page {chemicalPage} of {chemicalPages}
                </span>
              </div>

              <div className="digital-avatar-reason-card">
                <strong>Bio Lab Selection Attached</strong>
                <p>
                  Elements: {selectedBioElements.length} · Compound:{" "}
                  {selectedBioCompound?.name || "None"} · Proteins:{" "}
                  {selectedBioProteins.length} · Medicines:{" "}
                  {selectedBioMedicines.length}
                </p>
                <p>
                  This simulation runs only on a digital avatar. No human,
                  animal, or living organism is involved.
                </p>

                <button
                  type="button"
                  className="impact-secondary-btn"
                  onClick={() => setActiveTab("chemicals")}
                >
                  Add Bio Compound / Protein / Medicine
                </button>
              </div>

              {selectedPanelOpen && (
                <div className="selected-drugs-modal">
                  <div className="selected-drugs-card">
                    <div className="selected-drugs-head">
                      <div>
                        <h2>Selected Drug Combination</h2>
                        <p>
                          {selectedChemicals.length} medicines selected for
                          simulation.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setSelectedPanelOpen(false)}
                      >
                        ✕
                      </button>
                    </div>

                    {selectedChemicals.length === 0 ? (
                      <div className="research-empty">
                        No selected medicines yet.
                      </div>
                    ) : (
                      <div className="selected-drug-list">
                        {selectedChemicals.map((chem) => (
                          <div key={chem.id} className="selected-drug-item">
                            <div>
                              <strong>{chem.name}</strong>
                              <span>{chem.category}</span>
                              <small>
                                {chem.formula || "Formula not provided"}
                              </small>
                            </div>

                            <div className="selected-dose-edit">
                              <input
                                type="number"
                                min="0"
                                value={doseMap[chem.id] || 100}
                                onChange={(e) =>
                                  setDoseMap((prev) => ({
                                    ...prev,
                                    [chem.id]: e.target.value,
                                  }))
                                }
                              />
                              <em>mg</em>
                            </div>

                            <button
                              type="button"
                              className="remove-selected-drug"
                              onClick={() => {
                                setSelectedChemicals((prev) =>
                                  prev.filter((x) => x.id !== chem.id)
                                );
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="selected-drugs-actions">
                      <button
                        type="button"
                        className="impact-secondary-btn"
                        onClick={() => setSelectedPanelOpen(false)}
                      >
                        Continue Selecting
                      </button>

                      <button
                        type="button"
                        className="research-primary"
                        onClick={() => {
                          setSelectedPanelOpen(false);
                          runSimulation();
                        }}
                        disabled={loading}
                      >
                        {loading ? "Running..." : "Run Simulation"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="chemical-grid">
                {chemicals.length === 0 ? (
                  <div className="research-empty">
                    No chemicals found. Try search, A-Z filter, or add one from
                    Chemical Library.
                  </div>
                ) : (
                  chemicals.map((chem) => {
                    const selected = selectedChemicals.some(
                      (c) => c.id === chem.id
                    );

                    return (
                      <div
                        key={chem.id}
                        className={`chemical-card ${
                          selected ? "selected" : ""
                        }`}
                        onClick={() => toggleChemical(chem)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") toggleChemical(chem);
                        }}
                      >
                        <div className="chemical-card-top">
                          <strong>{chem.name}</strong>
                          <span>{chem.category}</span>
                        </div>

                        <p>{chem.formula || "Formula not provided"}</p>

                        {selected && (
                          <div
                            className="dose-row"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <label>Dose</label>
                            <input
                              type="number"
                              min="0"
                              value={doseMap[chem.id] || ""}
                              onChange={(e) =>
                                setDoseMap((prev) => ({
                                  ...prev,
                                  [chem.id]: e.target.value,
                                }))
                              }
                            />
                            <span>mg</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="chemical-pagination">
                <button
                  type="button"
                  disabled={chemicalPage <= 1}
                  onClick={() => goChemicalPage(chemicalPage - 1)}
                >
                  Previous
                </button>

                <span>
                  {chemicalPage} / {chemicalPages}
                </span>

                <button
                  type="button"
                  disabled={chemicalPage >= chemicalPages}
                  onClick={() => goChemicalPage(chemicalPage + 1)}
                >
                  Next
                </button>
              </div>
            </section>

            {activeResult && (
              <SimulationResultPanel
                result={activeResult}
                overallScore={overallScore}
                gender={gender || activeResult.gender}
                calculatedBmi={calculatedBmi}
                title="3D Human Body Impact Heatmap"
                selectedPatient={selectedPatient}
                avatarFaceProfile={avatarFaceProfile}
                selectedChemicals={selectedChemicalsWithDose}
                patientResearchContext={patientResearchContext}
                onImpactsFromMedgen={mergeMedgenImpactsIntoResult}
                onDownloadCsv={() => exportResultOrgansCsv(activeResult)}
              />
            )}
          </div>

          <div className="research-right">
            <aside className="research-card patient-context-pro sticky-panel">
              <h2>Patient Context</h2>
              <p className="muted">
                Used for BMI/body-factor adjustment, history-aware simulation,
                and avatar mapping.
              </p>

              <label className="research-label">Select Patient</label>
              <select
                className="research-input"
                value={patientId}
                onChange={(e) => handlePatientChange(e.target.value)}
              >
                <option value="">Normal-body baseline / no patient</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name}
                    {patient.age ? ` · ${patient.age} yrs` : ""}
                    {patient.gender ? ` · ${patient.gender}` : ""}
                    {patient.relation ? ` · ${patient.relation}` : ""}
                  </option>
                ))}
              </select>

              {patientId && (
                <div className="selected-patient-chip">
                  Selected:{" "}
                  <strong>
                    {selectedPatient?.name || `Patient #${patientId}`}
                  </strong>
                </div>
              )}

              <label className="research-label">
                Patient Session / Medgen Context
              </label>
              <select
                className="research-input"
                value={selectedSessionId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedSessionId(value);

                  const session = patientSessions.find(
                    (item) => String(item.id) === String(value)
                  );

                  setSelectedSessionContext(session || null);
                }}
              >
                <option value="">No session selected</option>
                {patientSessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    #{session.id} ·{" "}
                    {session.title ||
                      session.created_at ||
                      session.createdAt ||
                      "AI generated session"}
                  </option>
                ))}
              </select>

              {selectedSessionContext && (
                <div className="digital-avatar-reason-card">
                  <strong>Session Context Attached</strong>
                  <p>
                    Digital avatar simulation will include patient session
                    context, AI-generated analysis, questionnaire history, and
                    selected medicine/compound mappings.
                  </p>
                </div>
              )}

              <label className="research-label">Gender</label>
              <select
                className="research-input"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Not specified</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>

              <div className="biometric-row">
                <div>
                  <label className="research-label">Height CM</label>
                  <input
                    className="research-input"
                    type="number"
                    min="0"
                    placeholder="170"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                  />
                </div>

                <div>
                  <label className="research-label">Weight KG</label>
                  <input
                    className="research-input"
                    type="number"
                    min="0"
                    placeholder="70"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                  />
                </div>
              </div>

              {calculatedBmi && (
                <div className="bmi-chip">
                  BMI <strong>{calculatedBmi}</strong>
                </div>
              )}

              <PatientResearchContextPanel
                patientId={patientId}
                selectedPatient={selectedPatient}
                onContextChange={setPatientResearchContext}
              />

              <div className="research-face-summary-card">
                <div>
                  <span className="research-face-kicker">Avatar Face</span>
                  <strong>
                    {avatarFaceProfile
                      ? "Face profile attached"
                      : "No face profile"}
                  </strong>
                  <p>
                    {avatarFaceProfile
                      ? "Face and eye reference is attached to this digital avatar simulation."
                      : "Open scanner to capture avatar face and eye reference."}
                  </p>
                </div>

                <div className="research-face-summary-bottom">
                  <span>
                    Quality:{" "}
                    <b>{avatarFaceProfile?.capture_quality ?? "—"}</b>
                  </span>

                  <button
                    type="button"
                    className="research-primary research-face-open-btn"
                    onClick={() => setFaceScannerOpen(true)}
                  >
                    {avatarFaceProfile ? "View / Recapture" : "Open Face Scanner"}
                  </button>
                </div>
              </div>

              <button
                className="research-primary"
                disabled={loading}
                onClick={runSimulation}
                type="button"
              >
                {loading ? "Running..." : "Run Simulation"}
              </button>
            </aside>
          </div>
        </div>
      )}

      {activeTab === "chemicals" && (
        <div className="bio-lab-library-page bio-studio-page">
          <section className="research-card wide bio-studio-shell">
            <div className="bio-studio-hero">
              <div>
                <p className="bio-studio-kicker">Molecular Research Studio</p>
                <h2>Build, mutate, bond, and test on the digital avatar</h2>
                <span>
                  Browse known elements, compounds, proteins, and medicines.
                  Then design a visual compound by adding atoms and
                  right-clicking nodes to create bonds. No human, animal, or
                  living organism testing.
                </span>
              </div>

              <div className="bio-studio-metrics">
                <strong>{selectedBioCompounds.length}</strong>
                <span>Queue compounds</span>
                <strong>{compoundDesigner.atoms.length}</strong>
                <span>Studio atoms</span>
              </div>
            </div>

            <div className="bio-library-search studio-search-row">
              <input
                value={bioSearch}
                onChange={(e) => setBioSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchBioLibrary()}
                placeholder={`Search ${bioLibraryMode}...`}
              />

              <button type="button" onClick={searchBioLibrary}>
                Search
              </button>
            </div>

            <div className="bio-library-tabs bio-library-tabs-pro">
              {["elements", "compounds", "proteins", "medicines"].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={bioLibraryMode === mode ? "active" : ""}
                  onClick={() => setBioLibraryMode(mode)}
                >
                  {mode}
                </button>
              ))}
            </div>

            <div className="bio-workbench-mode-tabs">
              {[
                ["explore", "Explore"],
                ["studio", "Compound Studio"],
                ["queue", "Avatar Queue"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={activeBioWorkbench === key ? "active" : ""}
                  onClick={() => setActiveBioWorkbench(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="digital-avatar-reason-card studio-reason-card">
              <strong>Research-only digital avatar workflow</strong>
              <p>
                Create visual molecule hypotheses, map proteins/medicines, ask
                Medgen for potential organ/system impact, then save a
                digital-avatar simulation. This is not diagnosis, prescription,
                or safety approval.
              </p>
            </div>

            <div className="avatar-test-queue-strip">
              <div>
                <strong>Digital Avatar Test Queue</strong>
                <span>
                  {selectedBioCompounds.length} compounds ·{" "}
                  {selectedBioMedicines.length} medicines ·{" "}
                  {selectedBioProteins.length} proteins ·{" "}
                  {selectedBioElements.length} elements
                </span>
              </div>

              <div className="avatar-test-actions">
                <button type="button" onClick={startBlankCompoundStudio}>
                  New Compound
                </button>
                <button
                  type="button"
                  onClick={() => setActiveBioWorkbench("queue")}
                >
                  Review Queue
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={runSimulation}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Run Avatar Simulation"}
                </button>
              </div>
            </div>

            {simulationSaveStatus && (
              <div className="studio-status-banner">{simulationSaveStatus}</div>
            )}

            {activeBioWorkbench === "explore" && (
              <div className="studio-explore-panel">
                {bioLibraryMode === "elements" && (
                  <div className="studio-elements-layout">
                    <BioPeriodicTable
                      elements={bioElements}
                      selectedElements={selectedBioElements}
                      onToggleElement={(element) => {
                        toggleBioElement(element);
                        setCompoundDesigner((prev) => {
                          const nextIndex = prev.atoms.length;
                          const newAtom = {
                            id: `draft-${Date.now()}`,
                            atom_label: `${element.symbol}${nextIndex + 1}`,
                            element_symbol: element.symbol,
                            element_name: element.name,
                            atomic_number: element.atomic_number,
                            node_type: "element",
                            metadata_json: {
                              added_by_researcher: true,
                              source: "periodic_table",
                            },
                          };

                          const atoms = [...prev.atoms, newAtom];

                          return {
                            ...prev,
                            atoms,
                            formula: buildFormulaFromAtoms(atoms),
                          };
                        });
                        setActiveBioWorkbench("studio");
                      }}
                    />

                    <div className="studio-side-help">
                      <h3>Element action</h3>
                      <p>
                        Click any element to add it to the compound studio. Then
                        right-click atoms in the graph to create bonds.
                      </p>
                    </div>
                  </div>
                )}

                {bioLibraryMode === "compounds" && (
                  <div className="bio-library-layout studio-compound-layout">
                    <div className="bio-library-list studio-library-list">
                      {bioCompounds.map((compound) => (
                        <button
                          key={compound.id}
                          type="button"
                          className={
                            selectedBioCompound?.id === compound.id
                              ? "bio-library-item active"
                              : "bio-library-item"
                          }
                          onClick={() => loadBioCompoundDetail(compound.id)}
                        >
                          <div>
                            <strong>{compound.name}</strong>
                            <span>
                              {compound.display_formula ||
                                compound.formula ||
                                "No formula"}
                            </span>
                          </div>
                          <em>{compound.compound_type}</em>
                        </button>
                      ))}
                    </div>

                    <div className="studio-molecule-preview">
                      <div className="studio-preview-actions">
                        <button
                          type="button"
                          onClick={() =>
                            selectedBioCompoundDetail &&
                            addBioCompoundToAvatar(selectedBioCompoundDetail)
                          }
                          disabled={!selectedBioCompoundDetail?.compound}
                        >
                          Add to Queue
                        </button>
                        <button
                          type="button"
                          className="primary"
                          onClick={openSelectedCompoundInStudio}
                          disabled={!selectedBioCompoundDetail?.compound}
                        >
                          Modify in Studio
                        </button>
                      </div>

                      <BioMoleculeViewer3D
                        detail={selectedBioCompoundDetail}
                        selectedCompound={selectedBioCompound}
                        onSendToAvatar={(compound, detail) => {
                          addBioCompoundToAvatar(
                            detail || selectedBioCompoundDetail || { compound }
                          );
                          setActiveBioWorkbench("queue");
                        }}
                      />
                    </div>
                  </div>
                )}

                {bioLibraryMode === "proteins" && (
                  <BioCardGrid
                    items={bioProteins}
                    type="protein"
                    selectedItems={selectedBioProteins}
                    onToggle={toggleBioProtein}
                  />
                )}

                {bioLibraryMode === "medicines" && (
                  <BioCardGrid
                    items={bioMedicines}
                    type="medicine"
                    selectedItems={selectedBioMedicines}
                    onToggle={loadBioMedicineDetail}
                  />
                )}
              </div>
            )}

            {activeBioWorkbench === "studio" && (
              <MolecularResearchStudio
                elements={bioElements}
                compounds={bioCompounds}
                proteins={bioProteins}
                medicines={bioMedicines}
                patient={selectedPatient}
                patientResearchContext={patientResearchContext}
                onAskMedgen={async (payload) => {
                  const candidate = payload?.compound || {};
                  const graphNodes = candidate.graph_nodes || [];
                  const graphEdges = candidate.graph_edges || [];

                  const targetNodes = graphNodes
                    .filter((node) => node.node_type === "protein")
                    .map((node) => node.label)
                    .join(", ");

                  const candidateNodes = graphNodes
                    .filter((node) =>
                      [
                        "compound",
                        "biomolecule",
                        "medicine",
                        "vaccine",
                        "element",
                      ].includes(node.node_type)
                    )
                    .map((node) => node.label)
                    .slice(0, 12)
                    .join(", ");

                  const patientLine = selectedPatient
                    ? `${selectedPatient.name || "selected patient"}, ${
                        selectedPatient.age || "N/A"
                      }y, ${selectedPatient.gender || gender || "N/A"}, BMI ${
                        calculatedBmi || "N/A"
                      }`
                    : `No patient selected, BMI ${calculatedBmi || "N/A"}`;

                  return [
                    `Research-only Medgen prompt response for graph candidate: ${
                      candidate.name || "Research Candidate"
                    }.`,
                    `Formula/context: ${
                      candidate.formula || "GraphCandidate"
                    }. Nodes: ${graphNodes.length}. Links: ${graphEdges.length}.`,
                    `Candidate materials: ${
                      candidateNodes || "not explicitly selected"
                    }.`,
                    `Target/protein context: ${
                      targetNodes || "no explicit target protein selected"
                    }.`,
                    `Digital avatar context: ${patientLine}.`,
                    "Suggested hypothesis review: receptor/protein binding, enzyme metabolism, liver load, kidney clearance, cardiovascular stress, immune/inflammatory response, respiratory relevance, and interactions with patient/session history.",
                    "This is not safety approval, diagnosis, prescription, or clinical decision support. Researcher validation and external evidence are required before any real-world claim.",
                  ].join("\n\n");
                }}
                onAddToAvatarQueue={(item) => {
                  setSelectedBioCompounds((prev) => {
                    const exists = prev.some(
                      (entry) => entry.compound?.id === item.compound?.id
                    );
                    return exists ? prev : [...prev, item];
                  });
                  setSimulationSaveStatus(
                    "Studio candidate added to the Digital Avatar Test Queue. Run simulation when ready."
                  );
                  setActiveBioWorkbench("queue");
                }}
                onSaveDesignedCompound={async (payload) => {
                  setSimulationSaveStatus("Saving studio candidate...");
                  try {
                    await api.post("/api/research/bio/compounds/designed", {
                      name: payload.name,
                      formula: payload.formula,
                      compound_type: payload.compound_type,
                      description:
                        "Designed from Molecular Research Studio graph.",
                      atoms: payload.atoms || [],
                      bonds: payload.bonds || [],
                      metadata_json: {
                        source: "molecular_research_studio",
                        graph_nodes: payload.graph_nodes || [],
                        graph_edges: payload.graph_edges || [],
                        research_only: true,
                      },
                    });
                    setSimulationSaveStatus(
                      "Studio candidate saved successfully."
                    );
                    await loadBioCompounds(bioSearch);
                  } catch (err) {
                    console.warn("Designed compound API unavailable", err);
                    addDesignedCompoundToAvatar();
                    setSimulationSaveStatus(
                      "Save API is not ready yet. Candidate added locally to avatar queue."
                    );
                  }
                }}
                onRunAvatarSimulation={() => {
                  setActiveTab("simulate");
                  setTimeout(() => runSimulation(), 120);
                }}
              />
            )}

            {activeBioWorkbench === "queue" && (
              <AvatarQueuePanel
                selectedBioCompounds={selectedBioCompounds}
                selectedBioMedicines={selectedBioMedicines}
                selectedBioProteins={selectedBioProteins}
                selectedBioElements={selectedBioElements}
                onRemoveCompound={removeBioCompoundFromAvatar}
                onClearQueue={clearBioAvatarQueue}
                onRunSimulation={runSimulation}
                loading={loading}
              />
            )}
          </section>

          <form className="research-card" onSubmit={createChemical}>
            <h2>Manual Entry / Missing Chemical</h2>
            <p className="muted">
              Use only when a chemical is missing from the Bio Lab database or
              needs manual correction.
            </p>

            <label className="research-label">Name</label>
            <input
              className="research-input"
              required
              value={newChemical.name}
              onChange={(e) =>
                setNewChemical({ ...newChemical, name: e.target.value })
              }
              placeholder="Aspirin / Acetaminophen / CYP3A4..."
            />

            <label className="research-label">Formula</label>
            <input
              className="research-input"
              value={newChemical.formula}
              onChange={(e) =>
                setNewChemical({ ...newChemical, formula: e.target.value })
              }
              placeholder="C9H8O4"
            />

            <label className="research-label">Molecular Weight</label>
            <input
              className="research-input"
              type="number"
              value={newChemical.molecular_weight}
              onChange={(e) =>
                setNewChemical({
                  ...newChemical,
                  molecular_weight: e.target.value,
                })
              }
            />

            <label className="research-label">Category</label>
            <select
              className="research-input"
              value={newChemical.category}
              onChange={(e) =>
                setNewChemical({ ...newChemical, category: e.target.value })
              }
            >
              <option value="compound">Compound</option>
              <option value="drug">Drug</option>
              <option value="protein">Protein</option>
              <option value="enzyme">Enzyme</option>
              <option value="salt">Salt</option>
            </select>

            <label className="research-label">Description</label>
            <textarea
              className="research-input textarea"
              value={newChemical.description}
              onChange={(e) =>
                setNewChemical({ ...newChemical, description: e.target.value })
              }
            />

            <button className="research-primary" type="submit">
              Add Manual Chemical
            </button>
          </form>
        </div>
      )}

      {activeTab === "impacts" && (
        <div className="impact-workbench">
          <div className="impact-workbench-header">
            <div>
              <h2>Impact Mapping Workbench</h2>
              <p>
                Find a chemical, ask Medgen for organ impact, validate, then
                save.
              </p>
            </div>
          </div>

          <div className="impact-step-tabs">
            <button
              className={impactStep === "find" ? "active" : ""}
              onClick={() => setImpactStep("find")}
              type="button"
            >
              1. Find Chemical
            </button>

            <button
              className={impactStep === "validate" ? "active" : ""}
              onClick={() => setImpactStep("validate")}
              type="button"
            >
              2. Ask Medgen & Validate
            </button>

            <button
              className={impactStep === "chemical" ? "active" : ""}
              onClick={() => setImpactStep("chemical")}
              type="button"
            >
              3. Create / Correct Chemical
            </button>
          </div>

          {impactStep === "find" && (
            <section className="research-card impact-step-card">
              <h2>Find Existing Chemical</h2>
              <p className="muted">
                Search your local chemical library. Nothing is saved here.
              </p>

              <label className="research-label">Chemical Name</label>
              <div className="assist-search-row">
                <input
                  className="research-input"
                  placeholder="Example: Aspirin"
                  value={assistQuery}
                  onChange={(e) => setAssistQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && assistedSearch()}
                />
                <button type="button" onClick={assistedSearch}>
                  {assistLoading ? "Searching..." : "Auto Fill"}
                </button>
              </div>

              {assistResult && (
                <div className="assist-result-box digital-avatar-reason-card">
                  {assistResult.found ? (
                    <>
                      <strong>Found: {assistResult.chemical.name}</strong>
                      <p>
                        {assistResult.chemical.formula || "No formula"} ·{" "}
                        {assistResult.chemical.category}
                      </p>
                      <p>
                        {assistResult.impact_suggestions?.length || 0} impact
                        mappings found
                      </p>

                      <button
                        type="button"
                        className="impact-next-btn"
                        onClick={() => setImpactStep("validate")}
                      >
                        Continue to Ask Medgen & Validate
                      </button>
                    </>
                  ) : (
                    <>
                      <strong>No matching chemical found.</strong>
                      <p>Create it first, then return to validation.</p>

                      <button
                        type="button"
                        className="impact-next-btn"
                        onClick={() => setImpactStep("chemical")}
                      >
                        Create Chemical
                      </button>
                    </>
                  )}
                </div>
              )}
            </section>
          )}

          {impactStep === "validate" && (
            <div className="impact-validate-layout">
              <form
                className="research-card impact-step-card"
                onSubmit={createImpact}
              >
                <div className="research-card-head">
                  <div>
                    <h2>Ask Medgen & Validate Organ Impact</h2>
                    <p className="muted">
                      Medgen fills fields. Researcher reviews and saves manually.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="ask-llm-btn"
                    onClick={askLlmForImpact}
                    disabled={askLlmLoading}
                  >
                    {askLlmLoading ? "Asking Medgen..." : "Ask Medgen"}
                  </button>
                </div>

                <div className="impact-pro-grid">
                  <div className="impact-field wide">
                    <label>Chemical</label>
                    <select
                      className="research-input"
                      required
                      value={newImpact.chemical_id}
                      onChange={(e) =>
                        setNewImpact({
                          ...newImpact,
                          chemical_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select chemical</option>
                      {chemicals.map((chem) => (
                        <option key={chem.id} value={chem.id}>
                          {chem.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="impact-field">
                    <label>Organ</label>
                    <select
                      className="research-input"
                      value={newImpact.organ}
                      onChange={(e) =>
                        setNewImpact({ ...newImpact, organ: e.target.value })
                      }
                    >
                      {ORGAN_OPTIONS.map((organ) => (
                        <option key={organ} value={organ}>
                          {organ}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="impact-field">
                    <label>Body System</label>
                    <input
                      className="research-input"
                      placeholder="hepatic, renal, cardiac..."
                      value={newImpact.system}
                      onChange={(e) =>
                        setNewImpact({ ...newImpact, system: e.target.value })
                      }
                    />
                  </div>

                  <div className="impact-field">
                    <label>Impact Type</label>
                    <select
                      className="research-input"
                      value={newImpact.impact_type}
                      onChange={(e) =>
                        setNewImpact({
                          ...newImpact,
                          impact_type: e.target.value,
                        })
                      }
                    >
                      <option value="toxic">Toxic</option>
                      <option value="stress">Stress</option>
                      <option value="metabolism">Metabolism</option>
                      <option value="inhibit">Inhibit</option>
                      <option value="stimulate">Stimulate</option>
                      <option value="hormonal">Hormonal</option>
                      <option value="suppression">Suppression</option>
                      <option value="protective">Protective</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>

                  <div className="impact-field">
                    <label>Severity</label>
                    <input
                      className="research-input"
                      type="number"
                      min="0"
                      max="1"
                      step="0.05"
                      value={newImpact.severity}
                      onChange={(e) =>
                        setNewImpact({ ...newImpact, severity: e.target.value })
                      }
                    />
                  </div>

                  <div className="impact-field wide">
                    <label>Mechanism / Description</label>
                    <textarea
                      className="research-input textarea"
                      placeholder="Mechanism / description"
                      value={newImpact.description}
                      onChange={(e) =>
                        setNewImpact({
                          ...newImpact,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="impact-action-row">
                  <button
                    type="button"
                    className="impact-secondary-btn"
                    onClick={() => setImpactStep("find")}
                  >
                    Back
                  </button>

                  <button className="research-primary impact-save-btn">
                    Save Validated Mapping
                  </button>
                </div>
              </form>

              <aside className="medgen-side-panel">
                <h3>Medgen Research Assistant</h3>
                <p>
                  Medgen suggests structured organ impact fields. Researcher
                  validates before saving.
                </p>

                <div className="medgen-stream-box">
                  {impactRows.length > 0 ? (
                    <pre>
                      {medgenStreamText || "Structured impact table ready."}
                    </pre>
                  ) : medgenStreamText ? (
                    <pre>{medgenStreamText}</pre>
                  ) : askLlmLoading ? (
                    <div>Thinking...</div>
                  ) : (
                    <div>
                      No Medgen output yet.
                      <br />
                      Select a chemical and click <strong>Ask Medgen</strong>.
                    </div>
                  )}
                </div>

                {impactRows.length > 0 && (
                  <div className="structured-impact-table-box">
                    <div className="impact-table-head">
                      <strong>Structured Impact Table</strong>
                    </div>

                    <table className="structured-impact-table">
                      <thead>
                        <tr>
                          <th>Organ</th>
                          <th>System</th>
                          <th>Impact</th>
                          <th>Severity</th>
                          <th>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impactRows.map((row, index) => (
                          <tr
                            key={`${row.organ}-${index}`}
                            onClick={() =>
                              setNewImpact((prev) => ({
                                ...prev,
                                organ: row.organ || prev.organ,
                                system: row.system || prev.system,
                                impact_type:
                                  row.impact_type || prev.impact_type,
                                severity:
                                  row.severity !== undefined
                                    ? String(row.severity)
                                    : prev.severity,
                                description:
                                  row.description ||
                                  row.mechanism ||
                                  prev.description,
                              }))
                            }
                          >
                            <td>{formatInlineOrgan(row.organ)}</td>
                            <td>{row.system || "-"}</td>
                            <td>{row.impact_type}</td>
                            <td>{row.severity}</td>
                            <td>{row.confidence}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {llmSuggestion && (
                  <div className="medgen-final-box">
                    <strong>Raw Final Payload</strong>
                    <pre>{JSON.stringify(llmSuggestion, null, 2)}</pre>
                  </div>
                )}
              </aside>
            </div>
          )}

          {impactStep === "chemical" && (
            <form
              className="research-card impact-step-card"
              onSubmit={createChemical}
            >
              <h2>Create / Correct Chemical</h2>
              <p className="muted">
                Use this when a chemical is missing or needs correction.
              </p>

              <div className="impact-pro-grid">
                <div className="impact-field wide">
                  <label>Name</label>
                  <input
                    className="research-input"
                    required
                    value={newChemical.name}
                    onChange={(e) =>
                      setNewChemical({ ...newChemical, name: e.target.value })
                    }
                    placeholder="Chemical name"
                  />
                </div>

                <div className="impact-field">
                  <label>Formula</label>
                  <input
                    className="research-input"
                    value={newChemical.formula}
                    onChange={(e) =>
                      setNewChemical({
                        ...newChemical,
                        formula: e.target.value,
                      })
                    }
                    placeholder="C9H8O4"
                  />
                </div>

                <div className="impact-field">
                  <label>Molecular Weight</label>
                  <input
                    className="research-input"
                    type="number"
                    value={newChemical.molecular_weight}
                    onChange={(e) =>
                      setNewChemical({
                        ...newChemical,
                        molecular_weight: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="impact-field">
                  <label>Category</label>
                  <select
                    className="research-input"
                    value={newChemical.category}
                    onChange={(e) =>
                      setNewChemical({
                        ...newChemical,
                        category: e.target.value,
                      })
                    }
                  >
                    <option value="compound">Compound</option>
                    <option value="drug">Drug</option>
                    <option value="protein">Protein</option>
                    <option value="enzyme">Enzyme</option>
                    <option value="salt">Salt</option>
                  </select>
                </div>

                <div className="impact-field wide">
                  <label>Description</label>
                  <textarea
                    className="research-input textarea"
                    value={newChemical.description}
                    onChange={(e) =>
                      setNewChemical({
                        ...newChemical,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="impact-action-row">
                <button
                  type="button"
                  className="impact-secondary-btn"
                  onClick={() => setImpactStep("find")}
                >
                  Back
                </button>

                <button className="research-primary impact-save-btn">
                  Save Chemical
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {activeTab === "history" && (
        <section className="research-card wide history-pro-card">
          <h2>Simulation History</h2>
          <p className="muted">
            Click any session to render its saved body impact model below.
          </p>

          <div className="history-grid-pro">
            {history.length === 0 ? (
              <div className="research-empty">No simulations yet.</div>
            ) : (
              history.map((sim) => (
                <button
                  key={sim.id}
                  className={`history-tile ${
                    selectedHistory?.id === sim.id ? "active" : ""
                  }`}
                  onClick={() => openHistoryResult(sim)}
                  type="button"
                >
                  <strong>{sim.title}</strong>
                  <span>
                    {sim.created_at
                      ? new Date(sim.created_at).toLocaleString()
                      : "No date"}
                  </span>
                  <em>{sim.risk_level || "unknown"}</em>
                </button>
              ))
            )}
          </div>

          {selectedHistory?.result_payload && (
            <div id="history-result-view">
              <SimulationResultPanel
                result={selectedHistory.result_payload}
                overallScore={overallScore}
                gender={selectedHistory.result_payload.gender || gender}
                calculatedBmi={
                  selectedHistory.result_payload.bmi || calculatedBmi
                }
                title={`Saved Model: ${selectedHistory.title}`}
                selectedPatient={selectedPatient}
                selectedChemicals={selectedChemicalsWithDose}
                patientResearchContext={patientResearchContext}
                avatarFaceProfile={avatarFaceProfile}
                onImpactsFromMedgen={mergeMedgenImpactsIntoResult}
                onDownloadCsv={() =>
                  exportResultOrgansCsv(selectedHistory.result_payload)
                }
              />
            </div>
          )}
        </section>
      )}

      {activeTab === "dna" && (
        <DNAResearchStudio
          patient={selectedPatient}
          patientResearchContext={patientResearchContext}
          onAddToAvatarQueue={(item) => {
            setSelectedBioCompounds((prev) => {
              const exists = prev.some(
                (entry) => entry.compound?.id === item.compound?.id
              );
              return exists ? prev : [...prev, item];
            });

            setSimulationSaveStatus(
              "DNA medicine candidate added to Digital Avatar Test Queue."
            );

            setActiveTab("chemicals");
            setActiveBioWorkbench("queue");
          }}
          onRunAvatarSimulation={() => {
            setActiveTab("simulate");
            setTimeout(() => runSimulation(), 120);
          }}
        />
      )}

      {faceScannerOpen && (
        <div
          className="research-face-modal-backdrop"
          onClick={() => setFaceScannerOpen(false)}
        >
          <div
            className="research-face-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="research-face-modal-head">
              <div>
                <span>Digital Avatar Scanner</span>
                <strong>Face Capture Reference</strong>
                <p>
                  Capture face and eye geometry for the digital avatar
                  simulation. Research-only. Not diagnosis or biometric
                  verification.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setFaceScannerOpen(false)}
              >
                ✕
              </button>
            </div>

            <FaceCapturePanel
              patientId={patientId}
              onAvatarFaceProfileReady={(profile) => {
                setAvatarFaceProfile(profile);
              }}
              onSaved={(saved) => {
                console.log("Face profile saved:", saved);
              }}
            />

            <div className="research-face-modal-actions">
              <button
                type="button"
                className="impact-secondary-btn"
                onClick={() => setFaceScannerOpen(false)}
              >
                Close
              </button>

              <button
                type="button"
                className="research-primary"
                onClick={() => setFaceScannerOpen(false)}
              >
                Use This Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BioCardGrid({ items = [], type, selectedItems = [], onToggle }) {
  return (
    <div className="bio-card-grid studio-card-grid">
      {items.map((item) => {
        const selected = selectedItems.some((row) => row.id === item.id);
        const isMedicine = type === "medicine";

        return (
          <button
            key={item.id}
            type="button"
            className={selected ? "bio-select-card selected" : "bio-select-card"}
            onClick={() => onToggle(item)}
          >
            <strong>{isMedicine ? item.name : item.symbol}</strong>
            <span>
              {isMedicine
                ? item.formula || "Formula unavailable"
                : item.name}
            </span>
            <p>
              {isMedicine
                ? item.category || "Medicine"
                : `${item.protein_type || "Protein"} · ${
                    item.system || "System unknown"
                  }`}
            </p>
            <small>
              {isMedicine
                ? `Avatar mapping: ${
                    (item.affected_organs || []).join(", ") ||
                    "organ mapping pending"
                  }`
                : "May participate in receptor binding, enzyme metabolism, hormone signaling, transport, or drug interaction mapping."}
            </small>
          </button>
        );
      })}
    </div>
  );
}

function AvatarQueuePanel({
  selectedBioCompounds,
  selectedBioMedicines,
  selectedBioProteins,
  selectedBioElements,
  onRemoveCompound,
  onClearQueue,
  onRunSimulation,
  loading,
}) {
  const isEmpty =
    selectedBioCompounds.length === 0 &&
    selectedBioMedicines.length === 0 &&
    selectedBioProteins.length === 0 &&
    selectedBioElements.length === 0;

  return (
    <section className="avatar-queue-card studio-queue-card">
      <div className="compound-designer-head">
        <div>
          <p className="bio-studio-kicker">Digital Avatar Payload</p>
          <h2>Experiment Queue</h2>
          <span>
            Everything listed here will be saved into the simulation snapshot
            and used for avatar testing.
          </span>
        </div>

        <div className="avatar-test-actions">
          <button type="button" onClick={onClearQueue}>
            Clear Queue
          </button>
          <button
            type="button"
            className="primary"
            onClick={onRunSimulation}
            disabled={loading || isEmpty}
          >
            {loading ? "Running..." : "Run Avatar Simulation"}
          </button>
        </div>
      </div>

      {isEmpty ? (
        <div className="empty-studio-state">
          <strong>No experiment payload yet.</strong>
          <span>
            Add a medicine, protein, biomolecule, or designed compound first.
          </span>
        </div>
      ) : (
        <div className="queue-grid">
          {selectedBioCompounds.map((item) => (
            <div key={item.compound.id} className="queue-card compound">
              <strong>{item.compound.name}</strong>
              <span>
                {item.compound.formula ||
                  item.compound.display_formula ||
                  "No formula"}
              </span>
              <p>{item.source}</p>
              <small>
                Atoms: {(item.atoms || []).length} · Bonds:{" "}
                {(item.bonds || []).length}
              </small>
              <button
                type="button"
                onClick={() => onRemoveCompound(item.compound.id)}
              >
                Remove
              </button>
            </div>
          ))}

          {selectedBioMedicines.map((item) => (
            <div key={item.id} className="queue-card medicine">
              <strong>{item.name}</strong>
              <span>{item.generic_name || item.category || "Medicine"}</span>
              <p>
                {(item.affected_organs || []).join(", ") ||
                  "Organ mapping pending"}
              </p>
              <small>Targets: {(item.targets || []).length}</small>
            </div>
          ))}

          {selectedBioProteins.map((item) => (
            <div key={item.id} className="queue-card protein">
              <strong>{item.symbol}</strong>
              <span>{item.name}</span>
              <p>
                {item.primary_organ || "Systemic"} ·{" "}
                {item.system || "Unknown system"}
              </p>
            </div>
          ))}

          {selectedBioElements.map((item) => (
            <div key={item.id} className="queue-card element">
              <strong>{item.symbol}</strong>
              <span>{item.name}</span>
              <p>{item.category || "Element"}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SimulationResultPanel({
  result,
  overallScore,
  gender,
  calculatedBmi,
  title,
  selectedPatient,
  selectedChemicals,
  patientResearchContext,
  avatarFaceProfile,
  onImpactsFromMedgen,
  onDownloadCsv,
}) {
  return (
    <section className="research-card wide result-card result-card-pro">
      <div className="research-card-head">
        <div>
          <h2>{title}</h2>
          <p>
            Body factor: {result.body_factor || "N/A"} | BMI:{" "}
            {result.bmi || calculatedBmi || "N/A"}
          </p>
        </div>

        <div className={`risk-pill ${result.overall_risk}`}>
          {result.overall_risk || "unknown"}
        </div>
      </div>

      <div className="score-meter score-meter-pro">
        <div style={{ width: `${Math.min(overallScore * 100, 100)}%` }} />
      </div>

      <div className="heatmap-layout-pro hra-result-layout">
        <HraHumanBodyViewer
          organs={result.organs || {}}
          gender={gender === "female" ? "female" : "male"}
          transparent={true}
          faceProfile={avatarFaceProfile}
          showFaceMask={Boolean(avatarFaceProfile)}
        />

        <D3OrganTreePanel
          result={result}
          gender={gender === "female" ? "female" : "male"}
          patient={selectedPatient}
          selectedChemicals={selectedChemicals || []}
          patientResearchContext={patientResearchContext}
          onImpactsFromMedgen={onImpactsFromMedgen}
        />
      </div>

      <div className="organ-impact-table-card">
        <div className="organ-impact-table-head">
          <div>
            <h3>Organ Impact Rows</h3>
            <p>
              Same normalized organ is replaced, not duplicated. New organs are
              appended.
            </p>
          </div>

          <button
            type="button"
            className="impact-secondary-btn"
            onClick={onDownloadCsv}
          >
            Download CSV
          </button>
        </div>

        <div className="organ-impact-table-wrap">
          <table className="organ-impact-table">
            <thead>
              <tr>
                <th>Organ</th>
                <th>Risk</th>
                <th>Score</th>
                <th>Impact / System</th>
                <th>Reason</th>
              </tr>
            </thead>

            <tbody>
              {Object.entries(result.organs || {}).map(([organ, data]) => {
                const score = clamp01(Number(data.score || 0));
                const risk = data.risk || scoreToRiskFromPanel(score);
                const rowColor = getRiskColor(risk, score);

                return (
                  <tr key={organ}>
                    <td>
                      <div className="organ-cell-name">
                        <span style={{ background: rowColor }} />
                        <div>
                          <strong>{formatInlineOrgan(organ)}</strong>
                          <small>{organ}</small>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span
                        className="organ-risk-chip"
                        style={{
                          color: rowColor,
                          background: `${rowColor}18`,
                          borderColor: `${rowColor}40`,
                        }}
                      >
                        {risk}
                      </span>
                    </td>

                    <td>
                      <div className="organ-score-cell">
                        <div className="organ-score-track">
                          <i
                            style={{
                              width: `${Math.min(score * 100, 100)}%`,
                              background: rowColor,
                            }}
                          />
                        </div>
                        <b>{score.toFixed(2)}</b>
                      </div>
                    </td>

                    <td>
                      <span className="organ-inline-meta">
                        {data.impact_type || "impact"}
                        {data.system ? ` · ${data.system}` : ""}
                      </span>
                    </td>

                    <td>
                      <span className="organ-row-reason">
                        {Array.isArray(data.reasons) && data.reasons.length > 0
                          ? data.reasons[0]
                          : data.mechanism || "No reason recorded."}
                      </span>
                    </td>
                  </tr>
                );
              })}

              {Object.keys(result.organs || {}).length === 0 && (
                <tr>
                  <td colSpan="5">No organ rows available yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function formatInlineOrgan(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeMedgenRows(rows = []) {
  const byOrgan = new Map();

  rows.forEach((row) => {
    if (!row || typeof row !== "object") return;

    const organ = normalizeOrgan(row.organ);
    if (!organ || organ === "unknown") return;

    const description =
      row.description ||
      row.descrription ||
      row.descrript ||
      row.mechanism ||
      "Medgen generated organ impact suggestion.";

    byOrgan.set(organ, {
      organ,
      system: normalizeSystem(row.system),
      impact_type: normalizeImpactType(row.impact_type),
      severity: clamp01(Number(row.severity ?? row.score ?? 0.4)),
      confidence: clamp01(Number(row.confidence ?? 0.5)),
      mechanism: row.mechanism || "",
      description,
    });
  });

  return Array.from(byOrgan.values());
}

function scoreToRiskFromPanel(score) {
  if (score < 0.3) return "low";
  if (score < 0.6) return "moderate";
  return "high";
}

function getRiskColor(risk, score = 0) {
  const safeRisk = String(risk || scoreToRiskFromPanel(score)).toLowerCase();
  if (safeRisk.includes("high")) return "#ef4444";
  if (safeRisk.includes("moderate")) return "#f59e0b";
  if (safeRisk.includes("low")) return "#22c55e";
  return "#6366f1";
}

function normalizeOrgan(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";

  const normalized = raw
    .replaceAll(" ", "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/s$/, "");

  const aliases = {
    blood_vessels: "blood_vessel",
    blood_vessel: "blood_vessel",
    vascular: "blood_vessel",
    vasculature: "blood_vessel",
    artery: "blood_vessel",
    arteries: "blood_vessel",
    vein: "blood_vessel",
    veins: "blood_vessel",
    hepatic: "liver",
    renal: "kidney",
    cardiac: "heart",
    pulmonary: "lungs",
    lungs: "lungs",
    lung: "lungs",
    kidneys: "kidney",
    kidney: "kidney",
    livers: "liver",
    liver: "liver",
    intestines: "intestine",
    intestine: "intestine",
    brain: "brain",
    hearts: "heart",
    heart: "heart",
    stomachs: "stomach",
    stomach: "stomach",
  };

  return aliases[raw] || aliases[normalized] || normalized;
}

function normalizeSystem(value) {
  if (!value) return "";

  return String(value)
    .trim()
    .toLowerCase()
    .replaceAll("_", " ")
    .replace("renaal", "renal")
    .replace("urinarary", "urinary")
    .replace("urinaray", "urinary")
    .replace("digestive system", "gastrointestinal")
    .replace("cardiovascular system", "cardiovascular")
    .replace("central nervous system", "central_nervous_system")
    .replace("cns", "central_nervous_system")
    .trim();
}

function normalizeImpactType(value) {
  const raw = String(value || "stress").trim().toLowerCase();
  const allowed = new Set([
    "toxic",
    "stress",
    "inhibit",
    "stimulate",
    "hormonal",
    "suppression",
    "protective",
    "metabolism",
  ]);

  if (allowed.has(raw)) return raw;
  if (raw === "clearance" || raw === "excretion") return "metabolism";
  return "stress";
}

function clamp01(value) {
  if (!Number.isFinite(value)) return 0.5;
  return Math.max(0, Math.min(value, 1));
}

async function readMedgenStream(response, handlers = {}) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split("\n\n");
    buffer = events.pop() || "";

    for (const eventText of events) {
      const lines = eventText.split("\n");

      const eventLine = lines.find((line) => line.startsWith("event:"));
      const dataLine = lines.find((line) => line.startsWith("data:"));

      const eventName = eventLine?.replace("event:", "").trim();
      const dataRaw = dataLine?.replace("data:", "").trim();

      if (!eventName || dataRaw === undefined) continue;

      let data = {};
      try {
        data = dataRaw ? JSON.parse(dataRaw) : {};
      } catch (err) {
        console.warn("Invalid JSON from stream:", dataRaw);
        continue;
      }

      if (eventName === "start") {
        handlers.onStart?.(data);
      }

      if (eventName === "progress") {
        handlers.onProgress?.(data.message || "");
      }

      if (eventName === "final") {
        handlers.onFinal?.(data);
        return data;
      }

      if (eventName === "done") {
        handlers.onDone?.(data);
        return data;
      }

      if (eventName === "error") {
        throw new Error(data.message || "Medgen error");
      }
    }
  }
}